import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { collection, query, getDocs, orderBy, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import ArtAnalysisHotspots from '../components/ArtAnalysisHotspots';
import DiscoveryPanel from '../components/DiscoveryPanel';
import DiscoverElementsPanel from '../components/DiscoverElementsPanel';
import ArtActivitySection from '../components/ArtActivitySection';
import ClickableArtMovement from '../components/ClickableArtMovement';
import { saveArtHistoryAppreciationActivity, checkExistingActivity, ACTIVITY_TYPES } from '../utils/activityService';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { backendConfig, API_ENDPOINTS } from '../config/backendConfig';
import { enqueueAndPoll } from '../utils/jobPolling';
import BackButton from '../components/BackButton';
import { resolveImageUrl } from '../utils/urlResolver';
import { getArtMovementByName } from '../services/artMovementService';
import ArtformAboutStepper from '../components/ArtformAboutStepper';
import { ensureAgenticFormat } from '../utils/buildAgenticV2FromLegacy';
import { getArtformContent } from '../utils/artformService';
import { useFeatureQuota } from '../hooks/useFeatureQuota';
import UpgradeQuotaModal from '../components/UpgradeQuotaModal';

const isDev = process.env.NODE_ENV === 'development';
const VERIFIED_STORAGE_PREFIX = 'verified-artworks/';

function isStrictVerifiedBucketArtwork(artwork) {
  const path = typeof artwork?.verifiedImageStoragePath === 'string' ? artwork.verifiedImageStoragePath : '';
  const strictApproved = artwork?.verifiedStrictCommercialApproved === true;
  const hasVerifiedPath = path.startsWith(VERIFIED_STORAGE_PREFIX);
  return strictApproved && hasVerifiedPath;
}

const PLACEHOLDER_MOVEMENT_LABELS = new Set([
  '',
  'unknown',
  'uncategorized',
  'verification required',
  'verification-required',
  'not specified',
]);

/** Subject-matter / collection labels — not the same as art movement for `artforms/{key}` (Impressionism, Gothic, etc.). */
const ARTWORK_CATEGORY_SLUGS = new Set([
  'genre-scene',
  'genre-scene-painting',
  'genre-painting',
  'portrait',
  'landscape',
  'still-life',
  'floral',
  'animal-birds',
  'sacred',
  'figures',
  'abstract',
  'not-specified',
  'oil-on-canvas',
  'painting',
]);

function slugifyArtformKey(label = '') {
  return String(label || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/** When movement field is a placeholder, infer curriculum artform from well-known artists (not genre/category). */
function inferArtformKeyFromArtist(artwork) {
  const raw = String(artwork?.artistName || artwork?.artist || '');
  const name = raw.toLowerCase();
  if (!name) return '';
  // Ashcan School — urban American realism (e.g. Met American paintings)
  if (/\bjohn\s+sloan\b/.test(name)) return 'ashcan-school';
  if (/^sloan,?\s*j(ohn)?\b/i.test(raw.trim())) return 'ashcan-school';
  if (/\b(george\s+bellows|robert\s+henri|george\s+luks|everett\s+shinn|william\s+glackens)\b/.test(name)) {
    return 'ashcan-school';
  }
  return '';
}

function deriveArtformKey(movementName = '', artwork = null) {
  const movementToKeyMap = {
    'Impressionism': 'impressionism', 'Post-Impressionism': 'post-impressionism', 'Neo-Impressionism': 'neo-impressionism',
    'Cubism': 'cubism', 'Surrealism': 'surrealism', 'Expressionism': 'expressionism', 'Abstract Expressionism': 'abstract-expressionism',
    'Pop Art': 'pop-art', 'Art Nouveau': 'art-nouveau', 'Fauvism': 'fauvism', 'Minimalism': 'minimalism', 'Baroque': 'baroque',
    'Renaissance': 'renaissance', 'Romanticism': 'romanticism', 'Realism': 'realism', 'Abstract Art': 'abstract-art',
    'Contemporary': 'contemporary', 'Modernism': 'modernism',
    'History painting': 'history-painting', 'Religious art': 'religious-art', 'Glass-Stained': 'gothic', 'Gothic': 'gothic',
    'Ashcan School': 'ashcan-school', 'Ashcan school': 'ashcan-school', 'American Realism': 'american-realism',
    'Social realism': 'social-realism', 'Urban realism': 'social-realism',
  };

  const keyFromLabel = (label) => {
    const raw = String(label || '').trim();
    if (!raw || PLACEHOLDER_MOVEMENT_LABELS.has(raw.toLowerCase())) return '';
    const mapped = movementToKeyMap[raw];
    if (mapped) return mapped;
    const slug = slugifyArtformKey(raw);
    if (!slug || slug === 'verification-required') return '';
    if (ARTWORK_CATEGORY_SLUGS.has(slug)) return '';
    return slug;
  };

  let key = keyFromLabel(movementName);
  if (key) return key;

  const secondaryLabels = [
    artwork?.artMovementSecondary,
    artwork?.historicalMovement,
    artwork?.movementSchool,
    artwork?.school,
    artwork?.periodStyle,
    artwork?.derivedArtMovement,
    artwork?.styleMovement,
    artwork?.metMovement,
  ];
  for (const lab of secondaryLabels) {
    key = keyFromLabel(lab);
    if (key) return key;
  }

  key = keyFromLabel(artwork?.artMovement);
  if (key) return key;

  key = inferArtformKeyFromArtist(artwork);
  if (key) return key;

  const typeSlug = slugifyArtformKey(artwork?.typeOfArtwork || artwork?.artworkType || '');
  if (typeSlug && !ARTWORK_CATEGORY_SLUGS.has(typeSlug)) return typeSlug;

  return 'art-history';
}

/** Resolve artform doc key for preload/UI — never use Learn-flow category slugs (genre-scene) when movement is missing. */
function resolveArtformDocumentKey(targetArtwork = null) {
  if (!targetArtwork || typeof targetArtwork !== 'object') return 'art-history';
  const merged = {
    ...targetArtwork,
    artistName: targetArtwork.artistName || targetArtwork.artist || '',
  };
  let key = deriveArtformKey(merged.artMovement || '', merged);
  if (ARTWORK_CATEGORY_SLUGS.has(key)) {
    key = inferArtformKeyFromArtist(merged) || 'art-history';
  }
  return key || 'art-history';
}

function ensureFlowFriendlyPoints(payload = {}) {
  const out = { ...(payload || {}) };
  const hasAppreciation = Array.isArray(out.appreciation) && out.appreciation.length > 0;
  const hasPoints = Array.isArray(out.points) && out.points.length > 0;
  if (hasAppreciation || hasPoints) return out;

  const elements = out?.agenticFormat?.discover?.elements || out?.agenticAnalysis?.discover?.elements || [];
  if (!Array.isArray(elements) || elements.length === 0) return out;
  const points = elements.slice(0, 4).map((el, idx) => ({
    id: String(idx + 1),
    text: `${el?.name || `Element ${idx + 1}`}: ${el?.whyItMatters || el?.description || 'Look closely at this area and observe the artistic choices.'}`,
    label: el?.name || `Element ${idx + 1}`,
  }));
  out.points = points;
  out.appreciation = points.map((p) => p.text);
  return out;
}

/** Broad movement → browse bucket (filters only). Not suitable as artwork-type label — prefer typeOfArtwork. */
function movementToCategory(movement) {
  if (!movement) return '';
  const movementLower = movement.toLowerCase();
  if (movementLower.includes('renaissance') || movementLower.includes('baroque') || movementLower.includes('classical')) return 'Classical';
  if (movementLower.includes('impression') || movementLower.includes('post-impression')) return 'Modern';
  if (movementLower.includes('abstract') || movementLower.includes('cubism') || movementLower.includes('surreal')) return 'Abstract';
  if (movementLower.includes('contemporary') || movementLower.includes('pop') || movementLower.includes('minimal')) return 'Contemporary';
  return 'Other';
}

/** Loader overlay: show real artwork type (Landscape, Genre Scene, …), not generic "Other" from movement buckets. */
function resolveLoaderArtworkCategory(displayArtwork, selectedArtwork, locationState) {
  const aw = displayArtwork || selectedArtwork || {};
  const fromFlow = String(locationState?.artworkTypeLabel || '').trim();
  if (fromFlow) return fromFlow;
  const typeOf = String(aw.typeOfArtwork || aw.artworkType || '').trim();
  if (typeOf) return typeOf;
  const artCat = String(aw.artCategory || '').trim();
  if (artCat) return artCat;
  const movement = String(aw.artMovement || '').trim();
  const bucket = movementToCategory(movement);
  if (bucket && bucket !== 'Other') return bucket;
  if (movement) return movement;
  return 'Artwork';
}

// Cache functions removed - fetching directly from Firebase without cache
// This ensures artworks load even if cache module has issues

const ArtHistoryAppreciation = ({ onAnalysisComplete, primaryArtwork: primaryArtworkProp, step1AlreadyComplete: step1AlreadyCompleteProp, initialAppreciationResult: initialAppreciationResultProp, onAppreciationResult, onStep1Complete } = {}) => {
  // Use backendConfig for proper production URL handling
  const API_BASE = backendConfig.baseUrl;
  
  // Debug: Log when onStep1Complete is available
  useEffect(() => {
    if (onStep1Complete) {
      console.log('[ArtHistoryAppreciation] onStep1Complete prop is available');
    }
  }, [onStep1Complete]);
  
  console.log('[ArtHistoryAppreciation] API_BASE configured as:', API_BASE);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { checkQuotaBeforeUse, recordUsage, showUpgradeModal, dismissUpgradeModal, upgradeModalFeature } = useFeatureQuota();
  const [searchParams] = useSearchParams();
  
  // Check if user came from mentor dashboard
  const isFromMentorDashboard = user?.accountType === 'mentor' && user?.mentorType === 'single';
  
  // Check for mentor-selected artwork
  const [mentorArtwork, setMentorArtwork] = useState(null);
  const [myArtWorldArtwork, setMyArtWorldArtwork] = useState(null);
  
  useEffect(() => {
    const storedArtwork = localStorage.getItem('mentorSelectedArtwork');
    if (storedArtwork) {
      try {
        const artwork = JSON.parse(storedArtwork);
        setMentorArtwork(artwork);
        // Clear the stored artwork after loading
        localStorage.removeItem('mentorSelectedArtwork');
      } catch (error) {
        console.error('Error parsing mentor artwork:', error);
      }
    }
  }, []);

  // Load artwork from My Art World (state or query param)
  useEffect(() => {
    const loadMyArtWorldArtwork = async () => {
      console.log('[ArtHistoryAppreciation] Loading artwork from location.state:', location.state);
      console.log('[ArtHistoryAppreciation] Search params:', searchParams.get('artworkId'));
      
      // Try location.state first (flow uses primaryArtwork, other entry points use artwork)
      let artwork = location.state?.artwork || location.state?.primaryArtwork;

      if (artwork) {
        console.log('[ArtHistoryAppreciation] Found artwork in location.state:', artwork);
        setMyArtWorldArtwork(artwork);
        // If no selectedArtwork is set yet, use this one
        if (!selectedArtwork) {
          console.log('[ArtHistoryAppreciation] Setting as selectedArtwork');
          setSelectedArtwork(artwork);
        }
        return;
      }
      
      // If not in state, try query param
      const artworkId = searchParams.get('artworkId');
      if (artworkId && user) {
        console.log('[ArtHistoryAppreciation] Fetching artwork from Firestore, ID:', artworkId);
        try {
          // Try artworks collection first
          const artworkDoc = await getDoc(doc(db, 'artworks', artworkId));
          if (artworkDoc.exists()) {
            artwork = { id: artworkDoc.id, ...artworkDoc.data() };
            console.log('[ArtHistoryAppreciation] Found in artworks collection:', artwork);
          } else {
            // Try sharedArtworks collection
            const sharedDoc = await getDoc(doc(db, 'sharedArtworks', artworkId));
            if (sharedDoc.exists()) {
              const data = sharedDoc.data();
              artwork = {
                id: sharedDoc.id,
                title: data.artworkTitle || data.title,
                description: data.artworkDescription || data.description,
                imageUrl: data.artworkUrl || data.imageUrl,
                ...data
              };
              console.log('[ArtHistoryAppreciation] Found in sharedArtworks collection:', artwork);
            }
          }
        } catch (error) {
          console.error('[ArtHistoryAppreciation] Error fetching artwork from query param:', error);
        }
      }
      
      if (artwork) {
        console.log('[ArtHistoryAppreciation] Setting artwork:', artwork);
        setMyArtWorldArtwork(artwork);
        // If no selectedArtwork is set yet, use this one
        if (!selectedArtwork) {
          setSelectedArtwork(artwork);
        }
      } else {
        console.warn('[ArtHistoryAppreciation] No artwork found');
      }
    };
    
    loadMyArtWorldArtwork();
  }, [location.state, searchParams, user]);
  
  console.log('[ArtHistoryAppreciation] User context:', {
    accountType: user?.accountType,
    mentorType: user?.mentorType,
    isFromMentorDashboard,
    mentorArtwork
  });

  const [selectedArtwork, setSelectedArtwork] = useState(null);
  // Open modal if coming from co-learning space or by default
  const [showModal, setShowModal] = useState(true); // Open modal directly by default
  const [showAnalysis, setShowAnalysis] = useState(false); // Show analysis within modal

  // Ensure modal opens when coming from co-learning space or on initial load; when embedded in Learn Art History flow, keep picker closed.
  useEffect(() => {
    console.log('[ArtHistoryAppreciation] Location state:', location.state);
    console.log('[ArtHistoryAppreciation] showModal state:', showModal);
    if (location.state?.openModal === false || location.state?.embeddedInFlow === true) {
      setShowModal(false);
    } else if (location.state?.openModal === true || location.state?.openModal === undefined) {
      setShowModal(true);
    }
  }, [location.state]);
  const [isExpanded, setIsExpanded] = useState(false); // Fullscreen/expanded view

  const [artworks, setArtworks] = useState([]);
  const [filteredArtworks, setFilteredArtworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Filter states
  const [filters, setFilters] = useState({
    artMovement: '',
    artCategory: '',
    artForm: '',
    timePeriod: '',
    typeOfArtwork: ''
  });
  const [appreciationResult, setAppreciationResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [visibleSection, setVisibleSection] = useState(null); // 'appreciation' | 'technique' | 'activity'
  const [uploadPublic, setUploadPublic] = useState(false);
  const [activityFile, setActivityFile] = useState(null);
  const [isActivityPopupOpen, setIsActivityPopupOpen] = useState(false);
  const [showShareConfirmation, setShowShareConfirmation] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [uploadSuccessData, setUploadSuccessData] = useState(null);
  const [isArtMovementPopupOpen, setIsArtMovementPopupOpen] = useState(false);
  
  // Progressive loading states
  const [cachedHotspots, setCachedHotspots] = useState(null);
  const [hotspotsLoading, setHotspotsLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [expandedActivitySteps, setExpandedActivitySteps] = useState({}); // Track which activity steps are expanded
  const [artMovementData, setArtMovementData] = useState(null);
  const [artMovementLoading, setArtMovementLoading] = useState(false);
  const [flowActiveIndex, setFlowActiveIndex] = useState(null);
  const [flowActiveSection, setFlowActiveSection] = useState('appreciation');
  const [flowDetailExpanded, setFlowDetailExpanded] = useState(false); // mobile accordion
  const [hasStartedAnalysis, setHasStartedAnalysis] = useState(false);
  const [selectedOptionByPoint, setSelectedOptionByPoint] = useState({}); // pointKey -> optionIndex
  const [revealedByPoint, setRevealedByPoint] = useState({});             // pointKey -> boolean
  const [answeredHotspots, setAnsweredHotspots] = useState({});           // pointKey -> { selectedIndex, isCorrect }
  const [step1QuizData, setStep1QuizData] = useState(null);                // Step 1 quiz data from OpenAI
  const [step1QuizLoading, setStep1QuizLoading] = useState(false);
  const [showArtworkViewer, setShowArtworkViewer] = useState(false); // Control when artwork viewer appears
  // After "Start analysis", show art movement steps from Firebase after 15s while backend jobs run
  const [showMovementStepsAfterDelay, setShowMovementStepsAfterDelay] = useState(false);
  // Dedicated overlay: show loader immediately when user clicks Start analysis (embedded), hide after 15s or when job completes
  const [showLoaderOverlay, setShowLoaderOverlay] = useState(false);

  // Discovery Hotspots → Discovery Card → Technique Drilldown (structured outputs + vision)
  const [discoveryHotspots, setDiscoveryHotspots] = useState([]);          // hotspots[] from API, keyed by id in UI
  const [discoveryHotspotsLoading, setDiscoveryHotspotsLoading] = useState(false);
  const discoveryFailedForRef = useRef(null); // artwork key we already tried and failed (avoid retry loop on 404)
  const [selectedDiscoveryHotspotId, setSelectedDiscoveryHotspotId] = useState(null);
  const [techniqueDrilldown, setTechniqueDrilldown] = useState(null);
  const [techniqueDrilldownLoading, setTechniqueDrilldownLoading] = useState(false);
  const [techniqueDrilldownModalOpen, setTechniqueDrilldownModalOpen] = useState(false);
  const [techniqueSectionOpen, setTechniqueSectionOpen] = useState(null); // 'whatItIs' | 'howToTry' | 'impact' | null

  // IMPORTANT: embeddedInFlow / displayArtwork MUST be declared before any hooks
  // that reference them (to avoid temporal dead zone errors in production bundles).
  const embeddedInFlow = location.state?.embeddedInFlow === true;
  // In flow, show content when we have artwork from state or from parent prop (resume)
  const displayArtwork = embeddedInFlow ? (selectedArtwork || primaryArtworkProp) : selectedArtwork;
  const displayArtworkId = displayArtwork ? displayArtwork.id : null;
  const displayArtworkImageUrl = displayArtwork ? displayArtwork.imageUrl : null;
  const loaderArtworkName = (displayArtwork?.title || selectedArtwork?.title || 'selected artwork');
  const loaderArtworkCategory = resolveLoaderArtworkCategory(displayArtwork, selectedArtwork, location.state);

  // Fetch artworks from artHistoryArtworks collection
  const fetchArtworks = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 [ART HISTORY] Fetching artworks from Firebase...');
      
      // Skip cache operations - fetch directly from Firebase to ensure reliability
      
      // Try with orderBy first (for artworks with createdAt)
      let querySnapshot;
      try {
        const q = query(
          collection(db, 'artHistoryArtworks'),
          where('verifiedStrictCommercialApproved', '==', true),
          orderBy('createdAt', 'desc')
        );
        querySnapshot = await getDocs(q);
        console.log('✅ [ART HISTORY] Query with orderBy succeeded:', querySnapshot.docs.length, 'documents');
      } catch (orderByError) {
        console.warn('⚠️ [ART HISTORY] orderBy query failed, trying without orderBy:', orderByError);
        // Fallback: fetch without orderBy if createdAt index is missing
        const q = query(
          collection(db, 'artHistoryArtworks'),
          where('verifiedStrictCommercialApproved', '==', true)
        );
        querySnapshot = await getDocs(q);
        console.log('✅ [ART HISTORY] Query without orderBy succeeded:', querySnapshot.docs.length, 'documents');
      }
      
      const artworksData = querySnapshot.docs
        .map(doc => {
          const data = doc.data();
          // Ensure createdAt exists (use updatedAt or current time as fallback)
          if (!data.createdAt) {
            data.createdAt = data.updatedAt || new Date();
          }
          return {
            id: doc.id,
            ...data
          };
        })
        .filter((artwork) => isStrictVerifiedBucketArtwork(artwork));
      
      // Sort by createdAt if available (client-side fallback)
      artworksData.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.getTime ? a.createdAt.getTime() : 0);
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.getTime ? b.createdAt.getTime() : 0);
        return bTime - aTime; // Descending order
      });
      
      // Skip caching - artworks will be fetched fresh each time
      // This ensures reliability even if cache module has issues
      
      console.log('✅ [ART HISTORY] Fetched art history artworks:', artworksData.length);
      if (artworksData.length > 0) {
        console.log('📋 [ART HISTORY] Sample artwork:', artworksData[0]);
        console.log('📋 [ART HISTORY] All artworks:', artworksData);
      } else {
        console.warn('⚠️ [ART HISTORY] No artworks found in collection!');
        console.warn('⚠️ [ART HISTORY] Check Firebase console - artHistoryArtworks collection exists?');
      }
      
      setArtworks(artworksData);
      setFilteredArtworks(artworksData);
    } catch (error) {
      console.error('❌ [ART HISTORY] Error fetching art history artworks:', error);
      console.error('Error details:', error.message, error.code);
      console.error('Full error:', error);
      setArtworks([]);
      setFilteredArtworks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Apply filters
  const applyFilters = useCallback(() => {
    let filtered = artworks;
    console.log('Starting with artworks:', filtered.length);

    // Strict compliance gate: only pipeline-approved artworks in verified bucket.
    // This excludes default-bucket legacy images and any rows not strictly approved.
    const compliantArtworks = filtered.filter((artwork) => isStrictVerifiedBucketArtwork(artwork));
    if (compliantArtworks.length > 0) {
      console.log(
        '[ART HISTORY] Using only strict-approved verified-bucket artworks:',
        compliantArtworks.length,
        'of',
        filtered.length
      );
      filtered = compliantArtworks;
    } else {
      console.warn(
        '[ART HISTORY] No strict-approved verified-bucket artworks found. Keeping full set for visibility.'
      );
    }

    // Apply other filters
    if (filters.artMovement) {
      filtered = filtered.filter(artwork => 
        artwork.artMovement && artwork.artMovement === filters.artMovement
      );
      console.log('After art movement filter:', filtered.length, 'filter:', filters.artMovement);
    }

    if (filters.artCategory) {
      filtered = filtered.filter(artwork => 
        (artwork.artCategory || movementToCategory(artwork.artMovement)) === filters.artCategory
      );
      console.log('After category filter:', filtered.length, 'filter:', filters.artCategory);
    }

    if (filters.artForm) {
      filtered = filtered.filter(artwork => 
        artwork.artForm && artwork.artForm === filters.artForm
      );
      console.log('After art form filter:', filtered.length, 'filter:', filters.artForm);
    }

    if (filters.timePeriod) {
      filtered = filtered.filter(artwork => 
        artwork.timePeriod && artwork.timePeriod.toLowerCase().includes(filters.timePeriod.toLowerCase())
      );
      console.log('After time period filter:', filtered.length, 'filter:', filters.timePeriod);
    }

    if (filters.typeOfArtwork) {
      filtered = filtered.filter((artwork) => {
        const t = artwork.typeOfArtwork;
        if (!t) return false;
        if (t === filters.typeOfArtwork) return true;
        // Club legacy "Flower painting" under Floral
        if (filters.typeOfArtwork === 'Floral' && /flower painting/i.test(t)) return true;
        // Club legacy "Animal" labels under Animal/Birds
        if (filters.typeOfArtwork === 'Animal/Birds' && /^animal(\s*\/\s*birds)?s?$/i.test(t)) return true;
        return false;
      });
      console.log('After type filter:', filtered.length, 'filter:', filters.typeOfArtwork);
    }

    console.log('Final filtered artworks:', filtered.length);
    setFilteredArtworks(filtered);
    setCurrentIndex(0);
  }, [artworks, filters]);

  // Navigation functions
  const nextArtwork = () => {
    setCurrentIndex(prev => {
      const newIndex = prev < filteredArtworks.length - 1 ? prev + 1 : 0;
      console.log('Next artwork - current index:', prev, 'new index:', newIndex, 'total artworks:', filteredArtworks.length);
      return newIndex;
    });
  };

  const prevArtwork = () => {
    setCurrentIndex(prev => {
      const newIndex = prev > 0 ? prev - 1 : filteredArtworks.length - 1;
      console.log('Prev artwork - current index:', prev, 'new index:', newIndex, 'total artworks:', filteredArtworks.length);
      return newIndex;
    });
  };

  // Load artworks on component mount
  useEffect(() => {
    if (showModal && !showAnalysis && artworks.length === 0) {
      fetchArtworks();
    }
  }, [showModal, showAnalysis, artworks.length, fetchArtworks]);

  // Apply filters when they change
  useEffect(() => {
    if (artworks.length > 0) {
      applyFilters();
    }
  }, [applyFilters, artworks.length]);

  // Auto-select artwork when coming from Artmosphere Co-Learning Space
  useEffect(() => {
    if (!artworks.length) return;
    const pre = location.state?.preselectedArtwork;
    if (!pre) return;

    // Try to find matching artwork in the loaded list
    const match =
      artworks.find(a => a.id === pre.id) ||
      artworks.find(a => a.title === pre.title && a.artistName === pre.artist) ||
      null;

    if (match) {
      setSelectedArtwork(match);
      const idx = artworks.indexOf(match);
      if (idx >= 0) setCurrentIndex(idx);
    } else {
      // Fallback: construct a minimal artwork object so appreciation can still run
      setSelectedArtwork({
        id: pre.id || pre.imageUrl || 'external',
        title: pre.title || 'Art History Artwork',
        artistName: pre.artist || 'Unknown Artist',
        artMovement: pre.movement || '',
        imageUrl: pre.imageUrl,
      });
      setCurrentIndex(0);
    }
  }, [artworks, location.state, applyFilters]);

  // Art movement options for filtering (removed duplicates)
  const artMovements = [
    'Abstract Art', 'Abstract Expressionism', 'Academic Art', 'Ancient', 'Art Deco', 'Art Informel', 'Art Nouveau',
    'Avant-garde', 'Baroque', 'Bronze Age', 'Conceptual Art', 'Contemporary', 'Contemporary Realism', 'Cubism',
    'Dada', 'Dutch Golden Age', 'Early Renaissance', 'Environmental Art (Land art)', 'Expressionism', 'Fauvism',
    'Feminist Art', 'Futurism', 'High Renaissance', 'Impressionism', 'Iron Age', 'Kinetic art', 'Kitsch',
    'Light and Space', 'Magic Realism', 'Medieval', 'Minimalism', 'Modernism', 'Naturalism', 'Neo-Dada',
    'Neo-Expressionism', 'Neo-Impressionism', 'Neo-Minimalism (Neo-Geo)', 'Neo-Pop Art', 'Neo-Suprematism',
    'Neoclassicism', 'Neolithic', 'Op Art', 'Orientalism', 'Paleolithic', 'Photorealism (Super-Realism, Hyper-Realism)',
    'Pointillism', 'Pop Art', 'Post-Impressionism', 'Post-Minimalism', 'Post-Painterly Abstraction', 'Postmodern',
    'Realism', 'Renaissance', 'Rococo', 'Romanticism', 'Social Realism', 'Socialist Realism', 'Street art',
    'Surrealism', 'Symbolism', 'Synthetism', 'Tachisme', 'Tenebrism'
  ];

  const artForms = [
    'Oil on canvas', 'Oil on panel', 'Oil on board', 'Oil on paper', 'Oil on linen',
    'Acrylic on canvas', 'Acrylic on panel', 'Acrylic on board', 'Acrylic on paper',
    'Watercolor on paper', 'Watercolor on board', 'Watercolor on canvas',
    'Tempera on panel', 'Tempera on board', 'Tempera on canvas',
    'Fresco', 'Fresco secco', 'Buon fresco',
    'Gouache on paper', 'Gouache on board', 'Gouache on canvas',
    'Pastel on paper', 'Pastel on board', 'Pastel on canvas',
    'Charcoal on paper', 'Charcoal on board', 'Charcoal on canvas',
    'Pencil on paper', 'Pencil on board', 'Pencil on canvas',
    'Ink on paper', 'Ink on board', 'Ink on canvas', 'Ink on silk',
    'Mixed media', 'Collage', 'Assemblage', 'Installation', 'Digital art',
    'Photography', 'Sculpture', 'Relief', 'Bas-relief', 'High relief',
    'Marble', 'Bronze', 'Wood', 'Stone', 'Clay', 'Ceramic', 'Porcelain',
    'Glass', 'Metal', 'Plastic', 'Fiber', 'Textile', 'Fabric', 'Silk',
    'Print', 'Lithograph', 'Etching', 'Engraving', 'Woodcut', 'Linocut',
    'Screen print', 'Monotype', 'Digital print', 'Photographic print'
  ];

  const artworkTypes = [
    'Portrait', 'Self-portrait', 'Landscape', 'Seascape', 'Cityscape', 'Urban scene',
    'Still life', 'Genre scene', 'Historical', 'Mythological', 'Religious', 'Sacred',
    'Allegorical', 'Symbolic', 'Abstract', 'Non-representational', 'Figurative',
    'Narrative', 'Documentary', 'Political', 'Social commentary', 'Satirical',
    'Decorative', 'Ornamental', 'Architectural', 'Interior', 'Exterior',
    'Nature study', 'Botanical', 'Zoological', 'Anatomical', 'Scientific',
    'Fantasy', 'Surreal', 'Dreamlike', 'Imaginative', 'Fictional',
    'Realistic', 'Naturalistic', 'Photorealistic', 'Hyperrealistic',
    'Expressionistic', 'Emotional', 'Psychological', 'Introspective',
    'Minimalist', 'Geometric', 'Mathematical', 'Conceptual', 'Theoretical'
  ];

  // Generate SHA-1 hash (matching backend `crypto.createHash('sha1')` for cache keys)
  const generateSHA1Hash = async (text) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-1', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  /** Doc ID for `artworkAnalyses` — must match backend worker / routes (SHA1 of id|title|agentic). */
  const getAgenticAnalysisDocId = (art) =>
    generateSHA1Hash(`${art?.id || art?.imageUrl || ''}|${art?.title || ''}|agentic`);

  // Fetch Step 1 quiz data (cached or generate)
  const fetchStep1Quiz = async (artwork) => {
    if (!artwork?.imageUrl || !artwork?.title) return null;
    
    // Generate cache key matching backend (SHA1 hash)
    const cacheKeyString = `${artwork.id || artwork.imageUrl}|${artwork.title}|step1`;
    const step1CacheKey = await generateSHA1Hash(cacheKeyString);
    console.log('[Step1Quiz] Preparing Step 1 quiz for:', artwork.title, 'key:', step1CacheKey);
    
    try {
      // IMPORTANT: Do NOT read Step 1 quiz directly from Firestore here.
      // Firestore rules lock this collection; backend handles caching + security.
      // Always go through the Cloud Function API, which will reuse its own cache.
      console.log('[Step1Quiz] Requesting Step 1 quiz from backend (backend will use its cache if available)...');
      setStep1QuizLoading(true);
      
      // Get hotspot positions (default positions if not provided)
      const FLOW_HOTSPOT_POSITIONS = [
        { left: '25%', top: '28%' },
        { left: '55%', top: '35%' },
        { left: '38%', top: '65%' },
        { left: '78%', top: '72%' }
      ];
      
      const defaultLocationHints = ['left side of the scene', 'center area', 'further back', 'across the whole picture'];
      const hotspots = FLOW_HOTSPOT_POSITIONS.map((pos, i) => ({
        id: String(i + 1),
        x: parseFloat(pos.left) / 100,
        y: parseFloat(pos.top) / 100,
        areaLabel: `area ${i + 1}`,
        areaHint: defaultLocationHints[i] || '',
        elementLabel: 'this part of the artwork',
        elementLocationHint: defaultLocationHints[i] || '',
        elementType: '',
        visualHint: ''
      }));
      
      const endpoint = API_BASE.endsWith('/api') 
        ? `${API_BASE}/art-history-appreciation`
        : `${API_BASE}/api/art-history-appreciation`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          artwork,
          generateStep1Quiz: true,
          hotspots,
          skipCache: false
        })
      });
      
      // If cache hit, backend returns quiz JSON directly (status 200)
      if (response.ok && response.status === 200) {
        const quiz = await response.json();
        setStep1QuizLoading(false);
        setStep1QuizData(quiz);
        console.log('[Step1Quiz] Loaded Step 1 quiz from backend cache');
        return quiz;
      }

      // If job enqueued, poll job-status until complete
      if (response.status === 202) {
        const { jobId } = await response.json();
        console.log('[Step1Quiz] Job enqueued, polling for result:', jobId);

        const statusBase = API_BASE.endsWith('/api') ? API_BASE : `${API_BASE}/api`;
        const statusUrl = `${statusBase}/job-status/${jobId}`;

        const maxAttempts = 60;
        const intervalMs = 2000;

        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
          try {
            const statusResp = await fetch(statusUrl, { method: 'GET' });
            if (!statusResp.ok) {
              // 404 early on is normal while job doc propagates
              if (statusResp.status === 404 && attempt <= 5) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
                continue;
              }
              if (attempt === maxAttempts) {
                throw new Error(`Job status check failed with status ${statusResp.status}`);
              }
              await new Promise((resolve) => setTimeout(resolve, intervalMs));
              continue;
            }

            const statusJson = await statusResp.json();
            const state = statusJson.state || statusJson.status;

            if (state === 'failed') {
              const msg = statusJson.error || statusJson.message || 'Step 1 quiz job failed';
              throw new Error(msg);
            }

            const rawResult = statusJson.result != null
              ? statusJson.result
              : (statusJson.data != null ? statusJson.data : statusJson);

            // Extract points from any possible response shape (backend/Firestore may nest or flatten)
            function toPointsArray(maybe) {
              if (Array.isArray(maybe) && maybe.length > 0) return maybe;
              if (maybe && typeof maybe === 'object' && !Array.isArray(maybe)) {
                const keys = Object.keys(maybe).filter((k) => /^\d+$/.test(k)).sort((a, b) => Number(a) - Number(b));
                if (keys.length > 0) {
                  return keys.map((k) => maybe[k]).filter((p) => p && (p.question != null || p.options != null));
                }
              }
              return [];
            }

            // Check if job is complete - Step 1 quiz jobs return { step1Quiz: {...} } in result
            if (state === 'completed' || (rawResult && (rawResult.step1Quiz || rawResult.points))) {
              // Prefer nested step1Quiz; fallback to rawResult as quiz payload
              const quizPayload = rawResult.step1Quiz || rawResult;

              const fromQuiz = toPointsArray(quizPayload?.points);
              const fromRoot = toPointsArray(rawResult?.points);
              const fromData = toPointsArray(rawResult?.data?.points);
              const fromTopLevel = toPointsArray(statusJson.step1Quiz?.points);
              let points = fromQuiz.length >= 1 ? fromQuiz : (fromRoot.length >= 1 ? fromRoot : (fromData.length >= 1 ? fromData : fromTopLevel));

              // If still empty, search for any array of point-like objects anywhere in rawResult
              if (points.length === 0 && rawResult && typeof rawResult === 'object') {
                const walk = (obj, depth = 0) => {
                  if (depth > 5) return null;
                  if (Array.isArray(obj)) {
                    const looksLikePoints = obj.length >= 1 && obj.every(
                      (p) => p && typeof p === 'object' && (p.question != null || (Array.isArray(p.options) && p.options.length >= 1))
                    );
                    if (looksLikePoints) return obj;
                    return null;
                  }
                  if (obj && typeof obj === 'object') {
                    for (const key of Object.keys(obj)) {
                      const found = walk(obj[key], depth + 1);
                      if (found) return found;
                    }
                  }
                  return null;
                };
                const found = walk(rawResult);
                if (found) points = found;
              }

              console.log('[Step1Quiz] Received quiz payload:', {
                hasStep1Quiz: !!rawResult.step1Quiz,
                pointsLength: points.length,
                rawResultKeys: Object.keys(rawResult || {}),
                quizPayloadKeys: Object.keys(quizPayload || {})
              });

              // Normalize each point for UI (supports isBest, bestRevealLine, whyThisWorks, howToTry, tip)
              function normalizePoint(p, i) {
                const opts = Array.isArray(p.options) ? p.options : [];
                const options = opts.length >= 1
                  ? opts.map((o) => {
                      const obj = typeof o === 'object' && o != null ? o : { text: String(o) };
                      const isBest = obj.isBest === true || obj.isCorrect === true;
                      return { text: obj.text || '', isCorrect: isBest, isBest };
                    })
                  : [
                      { text: 'Option A', isCorrect: i === 0, isBest: i === 0 },
                      { text: 'Option B', isCorrect: false, isBest: false },
                      { text: 'Option C', isCorrect: false, isBest: false }
                    ];
                return {
                  id: p.id != null ? String(p.id) : String(i + 1),
                  question: typeof p.question === 'string' ? p.question : 'Look at this part of the artwork. If you wanted to create a similar effect in this style, what would you do?',
                  options,
                  explanation: typeof p.explanation === 'string' ? p.explanation : (typeof p.whyThisWorks === 'string' ? p.whyThisWorks : ''),
                  techniqueLearned: typeof p.techniqueLearned === 'string' ? p.techniqueLearned : '',
                  afterChoiceLine: typeof p.afterChoiceLine === 'string' ? p.afterChoiceLine : "Nice thinking — let's see what works best for this effect.",
                  correctRevealLine: typeof p.correctRevealLine === 'string' ? p.correctRevealLine : (typeof p.bestRevealLine === 'string' ? p.bestRevealLine : ''),
                  bestRevealLine: typeof p.bestRevealLine === 'string' ? p.bestRevealLine : (typeof p.correctRevealLine === 'string' ? p.correctRevealLine : ''),
                  techniqueName: typeof p.techniqueName === 'string' ? p.techniqueName : '',
                  whyThisWorks: typeof p.whyThisWorks === 'string' ? p.whyThisWorks : (typeof p.explanation === 'string' ? p.explanation : ''),
                  howToTry: Array.isArray(p.howToTry) ? p.howToTry.map((s) => (typeof s === 'string' ? s : String(s))) : [],
                  tip: typeof p.tip === 'string' ? p.tip : ''
                };
              }

              if (quizPayload && points.length >= 1) {
                const normalizedPoints = points.slice(0, 4).map(normalizePoint);
                while (normalizedPoints.length < 4) {
                  const i = normalizedPoints.length;
                  normalizedPoints.push({
                    id: String(i + 1),
                    question: `Look at this part of the artwork. If you wanted to create a similar effect in this style, what would you do?`,
                    options: [
                      { text: 'One way to create this effect', isCorrect: true, isBest: true },
                      { text: 'Another possibility', isCorrect: false, isBest: false },
                      { text: 'Something else', isCorrect: false, isBest: false }
                    ],
                    explanation: '',
                    techniqueLearned: '',
                    afterChoiceLine: "Nice thinking — let's see what works best for this effect.",
                    correctRevealLine: '',
                    bestRevealLine: '',
                    techniqueName: '',
                    whyThisWorks: '',
                    howToTry: [],
                    tip: ''
                  });
                }
                const normalizedPayload = {
                  ...(typeof quizPayload === 'object' && quizPayload !== null ? quizPayload : {}),
                  points: normalizedPoints
                };
                setStep1QuizLoading(false);
                setStep1QuizData(normalizedPayload);
                console.log('[Step1Quiz] Step 1 quiz set with', normalizedPayload.points.length, 'points');
                return normalizedPayload;
              }

              // Diagnostic: log actual response shape when points are missing
              try {
                const seen = new Set();
                const safeStringify = (obj, depth = 0) => {
                  if (depth > 4) return '…';
                  if (obj == null) return String(obj);
                  if (typeof obj !== 'object') return String(obj).slice(0, 80);
                  if (seen.has(obj)) return '[Circular]';
                  seen.add(obj);
                  if (Array.isArray(obj)) {
                    return `[${obj.length}]${obj.slice(0, 2).map((v) => safeStringify(v, depth + 1)).join(',')}${obj.length > 2 ? '…' : ''}`;
                  }
                  const keys = Object.keys(obj).slice(0, 15);
                  const pairs = keys.map((k) => `${k}:${safeStringify(obj[k], depth + 1)}`);
                  seen.delete(obj);
                  return `{${pairs.join(',')}}`;
                };
                console.warn('[Step1Quiz] Quiz payload missing or no points. Response shape:', safeStringify(rawResult));
                console.warn('[Step1Quiz] statusJson keys:', Object.keys(statusJson));
              } catch (_) { /* noop */ }
              setStep1QuizLoading(false);
              return null;
            }

            // Not complete yet, wait and poll again
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
          } catch (pollError) {
            if (attempt === maxAttempts) {
              setStep1QuizLoading(false);
              console.error('[Step1Quiz] Error polling job status:', pollError);
              return null;
            }
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
          }
        }

        setStep1QuizLoading(false);
        console.error('[Step1Quiz] Step 1 quiz polling timed out');
        return null;
      }

      // Any other status is an error
      setStep1QuizLoading(false);
      const errorText = await response.text();
      console.error('[Step1Quiz] Failed to request Step 1 quiz generation:', response.status, errorText);
      return null;
    } catch (error) {
      setStep1QuizLoading(false);
      console.error('[Step1Quiz] Error fetching Step 1 quiz:', error);
      return null;
    }
  };

  // Fetch cached hotspots for the selected artwork
  // Uses the art-history-appreciation endpoint which handles caching automatically
  const fetchCachedHotspots = async (artwork) => {
    // Also check for agentic analysis in artworkAnalyses collection
    try {
      const analysisKey = await getAgenticAnalysisDocId(artwork);
      const analysisRef = doc(db, 'artworkAnalyses', analysisKey);
      const analysisSnap = await getDoc(analysisRef);
      if (analysisSnap.exists()) {
        const agenticData = analysisSnap.data();
        console.log('[Appreciation] Found agentic analysis in cache', { artworkTitle: artwork.title });
        return { agenticAnalysis: agenticData, agenticFormat: agenticData };
      }
    } catch (error) {
      console.warn('[Appreciation] Error fetching agentic analysis:', error);
    }
    if (!artwork?.imageUrl || !artwork?.title) {
      console.log('[Hotspots] Missing artwork data:', { imageUrl: !!artwork?.imageUrl, title: !!artwork?.title });
      return null;
    }
    
    console.log('[Hotspots] Checking for cached art history appreciation data for:', artwork.title);
    console.log('[Hotspots] API_BASE:', API_BASE);
    
    try {
      console.log('[Hotspots] Making request to art-history-appreciation endpoint (will return cached if exists)');
      console.log('[Hotspots] Request payload:', { imageUrl: artwork.imageUrl, title: artwork.title });
      
      // Use art-history-appreciation endpoint which automatically checks cache
      // Use a short timeout - if cache exists, it should return quickly
      // If no cache, we abort and let the main analysis flow handle generation
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('[Hotspots] Cache check timeout - assuming no cache exists');
        controller.abort();
      }, 12000); // up to 12 seconds for cache lookup (backend may be cold)
      
      try {
        // API_BASE already includes /api, so don't add it again
        const endpoint = API_BASE.endsWith('/api') 
          ? `${API_BASE}/art-history-appreciation`
          : `${API_BASE}/api/art-history-appreciation`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            artwork: artwork,
            skipCache: false, // Check cache first
            skipActivity: !!location.state?.embeddedInFlow, // Step 1: key concepts + 4 points only; activity is in Step 3
          }),
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        console.log('[Hotspots] Response received, status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          if (data.fromCache === true) console.log('[ArtHistoryAppreciation] API response was from cache (fromCache: true)');
          console.log('[Hotspots] Cached data found:', !!(data.appreciation || data.points), data.fromCache === true ? '— fromCache: true' : '');
          // Return the full appreciation data if it exists
          if (data.appreciation || data.points) {
            return data; // Return full appreciation data
          }
        } else {
          console.log('[Hotspots] No cached data found (status:', response.status, ')');
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          console.log('[Hotspots] Cache check aborted (likely no cache - would start generation)');
        } else {
          console.error('[Hotspots] Error checking cache:', fetchError);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('[Hotspots] Cache check timed out, will proceed with full generation');
      } else {
        console.error('[Hotspots] Failed to check cached hotspots:', error);
      }
    }
    return null;
  };

  // Fetch discovery hotspots (12–18 elements with discovery cards + technique chips). Vision + strict JSON.
  const fetchDiscoveryHotspots = useCallback(async (artwork) => {
    if (!artwork?.imageUrl && !artwork?.title) return;
    const artworkKey = `${artwork.id || artwork.imageUrl || ''}|${artwork.title || ''}`;
    setDiscoveryHotspotsLoading(true);
    setDiscoveryHotspots([]);
    setSelectedDiscoveryHotspotId(null);
    try {
      const endpoint = API_BASE.endsWith('/api')
        ? `${API_BASE}/art-history-discovery-hotspots`
        : `${API_BASE}/api/art-history-discovery-hotspots`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artwork, sectionsMode: 'elements' }),
      });
      if (!res.ok) throw new Error(res.statusText || 'Discovery hotspots failed');
      discoveryFailedForRef.current = null; // success, clear failed marker
      const data = await res.json();
      const list = Array.isArray(data.hotspots) ? data.hotspots : [];
      // If the backend tells us it needsArtwork but still provides example hotspots,
      // we still want to render the new discovery UI using those example ideas.
      if (!list.length) {
        if (data.needsArtwork) {
          console.warn('[DiscoveryHotspots] needsArtwork=true but no hotspots returned');
        }
        setDiscoveryHotspots([]);
        return;
      }
      setDiscoveryHotspots(list);
    } catch (err) {
      console.error('[DiscoveryHotspots]', err);
      discoveryFailedForRef.current = artworkKey; // avoid retry loop on 404/failure
      setDiscoveryHotspots([]);
    } finally {
      setDiscoveryHotspotsLoading(false);
    }
  }, [API_BASE]);

  // Fetch technique drilldown for a chip tap (tapToLearnKey).
  const fetchTechniqueDrilldown = useCallback(async (tapToLearnKey, artwork) => {
    if (!tapToLearnKey || !artwork) return;
    setTechniqueDrilldownLoading(true);
    setTechniqueDrilldown(null);
    setTechniqueDrilldownModalOpen(true);
    try {
      const endpoint = API_BASE.endsWith('/api')
        ? `${API_BASE}/art-history-technique-drilldown`
        : `${API_BASE}/api/art-history-technique-drilldown`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ techniqueKey: tapToLearnKey, artwork }),
      });
      if (!res.ok) throw new Error(res.statusText || 'Technique drilldown failed');
      const data = await res.json();
      if (data.fromCache === true) console.log('[TechniqueDrilldown] API response was from cache (fromCache: true)');
      else if (data.fromCache === false) console.log('[TechniqueDrilldown] API response was not from cache (fromCache: false)');
      setTechniqueDrilldown(data);
    } catch (err) {
      console.error('[TechniqueDrilldown]', err);
      setTechniqueDrilldown({ key: tapToLearnKey, name: tapToLearnKey, whatItIs: 'Could not load.', howToTry: [] });
    } finally {
      setTechniqueDrilldownLoading(false);
    }
  }, [API_BASE]);

  // Display technique name without underscores (e.g. glow_light → Glow light).
  const formatTechniqueDisplayName = (name) => {
    if (!name || typeof name !== 'string') return name || 'Technique';
    return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Fetch discovery hotspots for the Hotspot → Card → Drilldown flow.
  // Trigger as soon as we know which artwork is being shown in Step 1.
  useEffect(() => {
    if (!displayArtwork || (!displayArtwork.imageUrl && !displayArtwork.title)) return;
    if (discoveryHotspotsLoading) return;
    // Avoid refetch if we already have hotspots for this artwork
    if (discoveryHotspots && discoveryHotspots.length > 0) return;
    const artworkKey = `${displayArtwork.id || displayArtwork.imageUrl || ''}|${displayArtwork.title || ''}`;
    // Don't retry in a loop if we already failed for this artwork (e.g. 404)
    if (discoveryFailedForRef.current === artworkKey) return;
    console.log('[DiscoveryHotspots] useEffect triggered for artwork:', displayArtwork.title);
    fetchDiscoveryHotspots(displayArtwork);
  }, [
    displayArtwork && displayArtwork.id,
    displayArtwork && displayArtwork.imageUrl,
    displayArtwork && displayArtwork.title,
    discoveryHotspotsLoading,
    discoveryHotspots?.length,
    fetchDiscoveryHotspots
  ]);

  const handleAnalyzeWithArtwork = async (artwork) => {
    console.log('[Appreciation] handleAnalyzeWithArtwork called', { artwork });
    // Some callers pass { artwork } instead of the raw artwork object; normalize here.
    const targetArtwork = artwork && artwork.artwork ? artwork.artwork : artwork;
    if (!targetArtwork) {
      console.error('[Appreciation] No artwork provided');
      setError('Please select an artwork to analyze');
      return;
    }
    const artworkId =
      targetArtwork.id ||
      targetArtwork.artworkId ||
      targetArtwork.imageId ||
      targetArtwork.imageUrl ||
      targetArtwork.title ||
      null;
    // Let backend dedupe by artworkId: same artwork should not consume quota again.
    // This avoids false blocking when user reopens previously analyzed artwork at limit.
    const usageResult = await recordUsage('artHistoryAppreciation', null, artworkId);
    if (!usageResult.ok || usageResult.overLimit) return;

    console.log('[Appreciation] Starting analysis process');
    if (location.state?.embeddedInFlow) {
      setHasStartedAnalysis(true);
      setShowLoaderOverlay(true); // Show loader immediately (reliable; hide after 15s or on job complete)
    }
    setIsAnalyzing(true);
    setError(null);
    setAppreciationResult(null);
    setCachedHotspots(null);
    setHotspotsLoading(true);
    setAnalysisLoading(location.state?.embeddedInFlow ? true : false);
    
    // Declare safetyTimeout at function level for proper scope
    let safetyTimeout = null;

    try {
      console.log('[Appreciation] Starting artwork appreciation analysis', { API_BASE, artworkId: artworkId || targetArtwork?.id });
      
      // Skip connectivity test - it's not needed and the endpoint doesn't exist
      console.log('[Appreciation] Proceeding directly to analysis...');

      // Embedded in flow: request appreciation + Step 1 quiz together so Discovery Panel is ready with hotspots (no "Preparing questions...")
      if (location.state?.embeddedInFlow) {
        const FLOW_HOTSPOT_POSITIONS = [
          { left: '25%', top: '28%' },
          { left: '55%', top: '35%' },
          { left: '38%', top: '65%' },
          { left: '78%', top: '72%' }
        ];
        const defaultLocationHints = ['left side of the scene', 'center area', 'further back', 'across the whole picture'];
        const hotspots = FLOW_HOTSPOT_POSITIONS.map((pos, i) => ({
          id: String(i + 1),
          x: parseFloat(pos.left) / 100,
          y: parseFloat(pos.top) / 100,
          areaLabel: `area ${i + 1}`,
          areaHint: defaultLocationHints[i] || '',
          elementLabel: 'this part of the artwork',
          elementLocationHint: defaultLocationHints[i] || '',
          elementType: '',
          visualHint: ''
        }));
        const appreciationEndpoint = API_BASE.endsWith('/api')
          ? `${API_BASE}/art-history-appreciation`
          : `${API_BASE}/api/art-history-appreciation`;

        // Show movement steps (from Firebase) after 15s so user has something to do while jobs run; hide loader so step1 appears
        setTimeout(() => {
          setShowMovementStepsAfterDelay(true);
          setShowLoaderOverlay(false);
        }, 15000);
        // Preload artform content so step1 screen shows with all movement details ready at 15s
        const artformKey = resolveArtformDocumentKey(targetArtwork);
        if (artformKey) getArtformContent(artformKey).catch(() => {});

        console.log('[Appreciation] Embedded flow: enqueueing appreciation (background); discovery continues via useEffect');
        enqueueAndPoll(
          appreciationEndpoint,
          {
          artwork: targetArtwork,
            discoveryOnly: true,
            generateStep1Quiz: false,
            skipCache: false,
            skipActivity: true,
            hotspots
          },
          {
            interval: 2000,
            maxAttempts: 150,
            onProgress: (status, job, attempts) => {
              const statusValue = status?.status || status?.state || status;
              if (statusValue === 'processing') {
                setHotspotsLoading(true);
                setAnalysisLoading(true);
              }
            }
          }
        )
          .then(async (data) => {
            setHotspotsLoading(false);
            if (data.fromCache === true) console.log('[ArtHistoryAppreciation] Artwork analysis result was from cache (fromCache: true)');
            else if (data.fromCache === false) console.log('[ArtHistoryAppreciation] Artwork analysis result was not from cache (fromCache: false)');
            let finalData = { ...data };
            if (!finalData.agenticFormat?.discover?.elements && !finalData.agenticAnalysis?.discover?.elements) {
              try {
                const artworkKey = await getAgenticAnalysisDocId(targetArtwork);
                const analysisRef = doc(db, 'artworkAnalyses', artworkKey);
                const analysisSnap = await getDoc(analysisRef);
                if (analysisSnap.exists()) {
                  const agenticData = analysisSnap.data();
                  if (agenticData?.discover?.elements?.length > 0) {
                    finalData.agenticFormat = agenticData;
                    finalData.agenticAnalysis = agenticData;
                  }
                }
              } catch (e) { /* ignore */ }
            }
            finalData = ensureFlowFriendlyPoints(ensureAgenticFormat(finalData, targetArtwork));
            setAppreciationResult(finalData);
            // Same as non-embedded path: worker returns rich discovery hotspots — replace generic/fallback pins from the early fetchDiscoveryHotspots call.
            if (finalData?.hotspots && Array.isArray(finalData.hotspots) && finalData.hotspots.length > 0) {
              setDiscoveryHotspots(finalData.hotspots);
            }
            setIsAnalyzing(false);
            setAnalysisLoading(false);
            if (finalData) {
              onAnalysisComplete?.();
              onAppreciationResult?.(finalData);
            }
          })
          .catch((error) => {
            console.error('[Appreciation] Embedded flow job failed', error);
            setHotspotsLoading(false);
            setIsAnalyzing(false);
            setAnalysisLoading(false);
            setError(`Failed to analyze artwork: ${error.message}`);
          });
        return;
      }

      // Step 1: Check for existing appreciation data first (non-embedded path)
      console.log('[Appreciation] Checking for existing appreciation data...');
      
      const cacheCheckPromise = fetchCachedHotspots(targetArtwork);
      const cacheCheckTimeout = new Promise((resolve) => setTimeout(() => resolve(null), 3000));
      
      const existingHotspots = await Promise.race([cacheCheckPromise, cacheCheckTimeout]);
      
      // Also check for agentic analysis in Firestore
      let agenticAnalysis = null;
      try {
        const artworkKey = await getAgenticAnalysisDocId(targetArtwork);
        const analysisRef = doc(db, 'artworkAnalyses', artworkKey);
        const analysisSnap = await getDoc(analysisRef);
        if (analysisSnap.exists()) {
          agenticAnalysis = analysisSnap.data();
          console.log('[Appreciation] Found agentic analysis in Firestore');
        }
      } catch (error) {
        console.warn('[Appreciation] Error fetching agentic analysis:', error);
      }
      
      setCachedHotspots(existingHotspots);
      setHotspotsLoading(false);

      if (existingHotspots && (existingHotspots.appreciation || existingHotspots.points || agenticAnalysis)) {
        console.log('[Appreciation] Found existing appreciation data, reusing it', existingHotspots.fromCache === true ? '(fromCache: true)' : '');
        console.log('[Appreciation] Cached data structure:', {
          hasAppreciation: !!existingHotspots.appreciation,
          appreciationLength: existingHotspots.appreciation?.length || 0,
          hasPoints: !!existingHotspots.points,
          pointsLength: existingHotspots.points?.length || 0,
          appreciationSample: existingHotspots.appreciation?.slice(0, 2) || []
        });
        
        // Merge agentic analysis if available (and log so console shows agentic in use)
        if (agenticAnalysis?.discover?.elements?.length > 0) {
          existingHotspots.agenticAnalysis = agenticAnalysis;
          existingHotspots.agenticFormat = agenticAnalysis;
          console.log('[Artmosphere] ✅ AGENTIC AI v2 IN USE — element-driven discovery,', agenticAnalysis.discover.elements.length, 'elements (from cache)');
        } else {
          // 100% RELIABLE: Build agenticFormat from legacy data if missing
          const builtAgentic = ensureAgenticFormat(existingHotspots, artwork);
          if (builtAgentic?.agenticFormat) {
            existingHotspots.agenticFormat = builtAgentic.agenticFormat;
            existingHotspots.agenticAnalysis = builtAgentic.agenticAnalysis;
            console.log('[Artmosphere] ✅ Built agenticFormat from legacy data (cache fallback)');
          }
        }

        // Only generate activity if we don't have it (and not in Learn Art History flow Step 1 — activity is in Step 3)
        let finalResult = null;
        const skipActivityInFlow = !!location.state?.embeddedInFlow;
        if (!skipActivityInFlow && !existingHotspots.activity) {
          console.log('[Appreciation] No existing activity found, generating activity only...');
          setAnalysisLoading(true);
          
          const activityController = new AbortController();
          const activityTimeoutId = setTimeout(() => activityController.abort(), 60000); // 60 seconds for activity
          
          try {
            // API_BASE already includes /api, so don't add it again
            const activityEndpoint = API_BASE.endsWith('/api') 
              ? `${API_BASE}/art-history-appreciation`
              : `${API_BASE}/api/art-history-appreciation`;
            const activityResponse = await fetch(activityEndpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                artwork: artwork,
                generateActivityOnly: true, // Flag to indicate we only need activity
              }),
              signal: activityController.signal,
            });
            clearTimeout(activityTimeoutId);
            
            if (activityResponse.ok) {
              const activityData = await activityResponse.json();
              console.log('[Appreciation] Activity generated successfully');
              
              // Combine existing appreciation with new activity
              finalResult = {
                appreciation: existingHotspots.appreciation,
                techniqueImprove: existingHotspots.techniqueImprove || [],
                activity: activityData.activity,
                points: existingHotspots.points || [],
                suggestion: existingHotspots.suggestion || '',
                techniques: existingHotspots.artAnalysis?.techniques || [],
                artAnalysis: existingHotspots.artAnalysis || null,
                concepts: existingHotspots.concepts || [],
                agenticFormat: existingHotspots.agenticFormat || null,
                agenticAnalysis: existingHotspots.agenticAnalysis || null
              };
              setAppreciationResult(finalResult);
              if (location.state?.embeddedInFlow && finalResult) {
                onAnalysisComplete?.();
                onAppreciationResult?.(finalResult);
              }
            } else {
              console.log('[Appreciation] Failed to generate activity, using existing data only');
              finalResult = {
                appreciation: existingHotspots.appreciation,
                techniqueImprove: existingHotspots.techniqueImprove || [],
                activity: null,
                points: existingHotspots.points || [],
                suggestion: existingHotspots.suggestion || '',
                techniques: existingHotspots.artAnalysis?.techniques || [],
                artAnalysis: existingHotspots.artAnalysis || null,
                concepts: existingHotspots.concepts || [],
                agenticFormat: existingHotspots.agenticFormat || null,
                agenticAnalysis: existingHotspots.agenticAnalysis || null
              };
              setAppreciationResult(finalResult);
              if (location.state?.embeddedInFlow && finalResult) {
                onAnalysisComplete?.();
                onAppreciationResult?.(finalResult);
              }
            }
          } catch (activityError) {
            clearTimeout(activityTimeoutId);
            console.error('[Appreciation] Error generating activity:', activityError);
            // Use existing data even if activity generation fails
            if (activityError.name === 'AbortError') {
              console.warn('[Appreciation] Activity generation timed out, using existing data without activity');
            }
            finalResult = {
              appreciation: existingHotspots.appreciation,
              techniqueImprove: existingHotspots.techniqueImprove || [],
              activity: null,
              points: existingHotspots.points || [],
              suggestion: existingHotspots.suggestion || '',
              techniques: existingHotspots.artAnalysis?.techniques || [],
              artAnalysis: existingHotspots.artAnalysis || null,
              concepts: existingHotspots.concepts || [],
              agenticFormat: existingHotspots.agenticFormat || null,
              agenticAnalysis: existingHotspots.agenticAnalysis || null
            };
            setAppreciationResult(finalResult);
            if (location.state?.embeddedInFlow && finalResult) {
              onAnalysisComplete?.();
              onAppreciationResult?.(finalResult);
            }
          }
        } else {
          console.log('[Appreciation] Found existing activity as well, using all cached data (or Step 1 flow: no activity)');
          finalResult = {
            appreciation: existingHotspots.appreciation,
            techniqueImprove: existingHotspots.techniqueImprove || [],
            activity: skipActivityInFlow ? null : (existingHotspots.activity || null),
            points: existingHotspots.points || [],
            suggestion: existingHotspots.suggestion || '',
            techniques: existingHotspots.artAnalysis?.techniques || [],
            artAnalysis: existingHotspots.artAnalysis || null,
            concepts: existingHotspots.concepts || [],
            agenticFormat: existingHotspots.agenticFormat || null,
            agenticAnalysis: existingHotspots.agenticAnalysis || null
          };
          setAppreciationResult(finalResult);
          if (location.state?.embeddedInFlow && finalResult) {
            onAnalysisComplete?.();
            onAppreciationResult?.(finalResult);
          }
        }

        // Save art history appreciation to activity dashboard (even when using cached data)
        // Skip when embedded in Learn Art History flow — flow is saved as single flow per artwork (artHistoryFlows)
        if (finalResult && user && !location.state?.embeddedInFlow) {
          try {
            console.log('[Appreciation] Checking if art history appreciation already exists in activity dashboard...');
            const existingActivity = await checkExistingActivity(artwork.imageUrl, ACTIVITY_TYPES.ART_HISTORY_APPRECIATION);
            
            if (existingActivity) {
              console.log('✅ Art history appreciation already exists in activity dashboard, skipping save to prevent duplicate. Activity ID:', existingActivity.id);
            } else {
              console.log('[Appreciation] No existing activity found, saving art history appreciation to activity dashboard...');
              const points = finalResult.points || [];
              const processedPoints = points.map(p => typeof p === 'string' ? p : (p?.text || p?.label || ''));
              
              const activityId = await saveArtHistoryAppreciationActivity({
                originalImageUrl: artwork.imageUrl,
                artwork: artwork,
                appreciation: finalResult.appreciation || [],
                points: processedPoints,
                suggestion: finalResult.suggestion || '',
                techniques: finalResult.techniques || [],
                artAnalysis: finalResult.artAnalysis || null,
                concepts: finalResult.concepts || [],
                activity: finalResult.activity || null,
                techniqueImprove: finalResult.techniqueImprove || [],
                title: `Art History: ${artwork.title}`,
                description: `Art appreciation analysis for ${artwork.title} by ${artwork.artistName}`,
                metadata: {
                  context: user.accountType === 'mentor' ? 'mentor' : 'student',
                  mentorId: user.accountType === 'mentor' ? user.uid : null,
                  studentId: user.accountType !== 'mentor' ? user.uid : null,
                  artistName: artwork.artistName,
                  artMovement: artwork.artMovement,
                  artworkTitle: artwork.title,
                  className: typeof window !== 'undefined' ? (localStorage.getItem('mentorClass') || user.className || '') : (user.className || ''),
                  section: typeof window !== 'undefined' ? (localStorage.getItem('mentorSection') || user.section || '') : (user.section || '')
                }
              });
              console.log('✅ Art history appreciation saved to activity dashboard with ID:', activityId);
              
              // Cache clearing removed - activities will refresh on next load
              console.log('✅ [ART HISTORY APPRECIATION] Activity saved successfully');
            }
          } catch (activityError) {
            console.warn('⚠️ Failed to check/save art history appreciation to activity dashboard:', activityError);
          }
        }
        
        setAnalysisLoading(false);
        setIsAnalyzing(false);
        return; // Exit early since we have all the data we need
      }

      // Step 2: No existing data found, generate everything with OpenAI
      console.log('[Appreciation] No existing appreciation data found, generating full analysis...');
      setAnalysisLoading(true);
      console.log('[Appreciation] Making analysis request with timeout');
      console.log('[Appreciation] Request payload:', { artwork: artwork });

      // Add safety timeout to always clear loading state (slightly above main request timeout)
      safetyTimeout = setTimeout(() => {
        console.warn('[Appreciation] Safety timeout reached (140s), forcing loading to end');
        setIsAnalyzing(false);
        setAnalysisLoading(false);
        setError('Request timed out. The analysis is taking longer than expected. Please try again.');
      }, 140000); // 140 seconds

      // UX OPTIMIZATION: Use background workers with polling to fix timeout issues
      console.log('[Appreciation] Enqueueing Art History Appreciation job...');
      setAnalysisLoading(true);
      
      // API_BASE already includes /api, so don't add it again
      const appreciationEndpoint = API_BASE.endsWith('/api') 
        ? `${API_BASE}/art-history-appreciation`
        : `${API_BASE}/api/art-history-appreciation`;
      
      console.log('[Appreciation] Calling enqueueAndPoll with endpoint:', appreciationEndpoint);
      console.log('[Appreciation] Request payload:', { 
        artworkTitle: artwork?.title,
        skipCache: false,
        skipActivity: !!location.state?.embeddedInFlow
      });
      
      const jobResult = await enqueueAndPoll(
        appreciationEndpoint,
        {
          artwork: targetArtwork,
          skipCache: false,
          skipActivity: !!location.state?.embeddedInFlow // Step 1: key concepts + 4 points only; activity in Step 3
        },
        {
          interval: 2000, // Poll every 2 seconds
          maxAttempts: 150, // Max 5 minutes
          onProgress: (status, job, attempts) => {
            let message = '';
            if (status === 'enqueued') message = 'Artmosphere is preparing your artwork for analysis...';
            else if (status === 'processing') message = `Artmosphere is deeply analyzing your artwork (attempt ${attempts})...`;
            else if (status === 'completed') message = 'Analysis complete! Preparing feedback...';
            console.log('[Appreciation] Job status:', status, message);
          }
        }
      );
      
      const result = jobResult; // jobResult is the actual data from the worker
      console.log('[Appreciation] Analysis complete, result:', result);
      if (result.fromCache === true) console.log('[ArtHistoryAppreciation] Artwork analysis result was from cache (fromCache: true)');
      else if (result.fromCache === false) console.log('[ArtHistoryAppreciation] Artwork analysis result was not from cache (fromCache: false)');
      
      // Check if agentic analysis is in the result
      if (result.agenticFormat || result.agenticAnalysis) {
        console.log('[Appreciation] Agentic analysis found in result');
      }
      
      if (safetyTimeout !== null) {
        clearTimeout(safetyTimeout);
        safetyTimeout = null;
      }
      
      // Result from polling is already parsed JSON
      console.log('[Appreciation] Result received from background worker:', result);
      
      // Clear safety timeout immediately after getting result
      if (safetyTimeout !== null) {
        clearTimeout(safetyTimeout);
        safetyTimeout = null;
      }
      
      // 100% RELIABLE: Ensure agenticFormat is ALWAYS present
      // Try multiple fallbacks: 1) from response, 2) from artworkAnalyses, 3) build from legacy
      let finalResult = { ...result };
      
      // Fallback 1: Try fetching from artworkAnalyses
      if (!finalResult.agenticFormat?.discover?.elements && !finalResult.agenticAnalysis?.discover?.elements) {
        try {
          const artworkKey = await getAgenticAnalysisDocId(targetArtwork);
          const analysisRef = doc(db, 'artworkAnalyses', artworkKey);
          const analysisSnap = await getDoc(analysisRef);
          if (analysisSnap.exists()) {
            const agenticData = analysisSnap.data();
            if (agenticData?.discover?.elements?.length > 0) {
              finalResult.agenticFormat = agenticData;
              finalResult.agenticAnalysis = agenticData;
              console.log('[Appreciation] ✅ Fetched agenticFormat from artworkAnalyses (polling)');
            }
          }
        } catch (error) {
          console.warn('[Appreciation] Error fetching agenticFormat from artworkAnalyses:', error);
        }
      }
      
      // Fallback 2: Build from legacy data (100% reliable - will ALWAYS work)
      finalResult = ensureFlowFriendlyPoints(ensureAgenticFormat(finalResult, targetArtwork));
      
      // Set result immediately to update UI
      // Log agentic v2 detection
      const agenticData = finalResult?.agenticFormat || finalResult?.agenticAnalysis;
      if (agenticData && agenticData.discover?.elements) {
        const n = agenticData.discover.elements.length;
        console.log('[Artmosphere] ✅ AGENTIC AI v2 IN USE — element-driven discovery,', n, 'elements');
        console.log('[Appreciation] ✅ Agentic v2 detected!', {
          version: agenticData.meta?.version || 'v2',
          elementsCount: n,
          artworkType: agenticData.meta?.artworkType,
          roleTitle: agenticData.discover.roleTitle
        });
      } else {
        console.warn('[Artmosphere] ⚠️ Agentic v2 not detected (should not happen with ensureAgenticFormat)');
      }
      setAppreciationResult(finalResult);
      setIsAnalyzing(false);
      setAnalysisLoading(false);
      setHotspotsLoading(false);
      // Use hotspots from job result when present (single-call backend returns discovery + step1 + appreciation)
      if (finalResult?.hotspots && Array.isArray(finalResult.hotspots) && finalResult.hotspots.length > 0) {
        setDiscoveryHotspots(finalResult.hotspots);
      }
      if (location.state?.embeddedInFlow && result) {
        onAnalysisComplete?.();
        onAppreciationResult?.(finalResult);
      }

      // Save art history appreciation to activity dashboard (non-blocking, in background)
      // Skip when embedded in Learn Art History flow — flow is saved as single flow per artwork (artHistoryFlows)
      if (result && user && !location.state?.embeddedInFlow) {
        // Don't await - let it run in background
        (async () => {
          try {
            console.log('[Appreciation] Checking if art history appreciation already exists in activity dashboard...');
            const existingActivity = await checkExistingActivity(artwork.imageUrl, ACTIVITY_TYPES.ART_HISTORY_APPRECIATION);
            
            if (existingActivity) {
              console.log('✅ Art history appreciation already exists in activity dashboard, skipping save to prevent duplicate. Activity ID:', existingActivity.id);
            } else {
              console.log('[Appreciation] No existing activity found, saving art history appreciation to activity dashboard...');
              const points = result.points || [];
              const processedPoints = points.map(p => typeof p === 'string' ? p : (p?.text || p?.label || ''));
              
              const activityId = await saveArtHistoryAppreciationActivity({
                originalImageUrl: artwork.imageUrl,
                artwork: artwork,
                appreciation: result.appreciation || [],
                points: processedPoints,
                suggestion: result.suggestion || '',
                techniques: result.artAnalysis?.techniques || [],
                artAnalysis: result.artAnalysis || null,
                concepts: result.concepts || [],
                activity: result.activity || null,
                techniqueImprove: result.techniqueImprove || [],
                title: `Art History: ${artwork.title}`,
                description: `Art appreciation analysis for ${artwork.title} by ${artwork.artistName}`,
                metadata: {
                  context: user.accountType === 'mentor' ? 'mentor' : 'student',
                  mentorId: user.accountType === 'mentor' ? user.uid : null,
                  studentId: user.accountType !== 'mentor' ? user.uid : null,
                  artistName: artwork.artistName,
                  artMovement: artwork.artMovement,
                  artworkTitle: artwork.title,
                  className: typeof window !== 'undefined' ? (localStorage.getItem('mentorClass') || user.className || '') : (user.className || ''),
                  section: typeof window !== 'undefined' ? (localStorage.getItem('mentorSection') || user.section || '') : (user.section || '')
                }
              });
              console.log('✅ Art history appreciation saved to activity dashboard with ID:', activityId);
            }
          } catch (activityError) {
            console.warn('⚠️ Failed to check/save art history appreciation to activity dashboard:', activityError);
          }
        })();
      }
    } catch (error) {
      console.error('[Appreciation] Error during analysis:', error);
      
      // Clear safety timeout if it exists
      if (safetyTimeout !== null) {
        clearTimeout(safetyTimeout);
        safetyTimeout = null;
      }
      
      // Provide more specific error messages
      let errorMessage = 'Failed to analyze artwork';
      if (error.name === 'AbortError') {
        errorMessage = 'Request timed out. Please try again.';
      } else if (error.message.includes('Backend server is not responding')) {
        errorMessage = 'Backend server is not responding. Please ensure the backend is running on port 3001.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        errorMessage = error.message || 'Failed to analyze artwork';
      }
      
      setError(errorMessage);
    } finally {
      console.log('[Appreciation] handleAnalyze finished, clearing loading states');
      // Clear safety timeout if it still exists
      if (safetyTimeout !== null) {
        clearTimeout(safetyTimeout);
        safetyTimeout = null;
      }
      setIsAnalyzing(false);
      setAnalysisLoading(false);
      setHotspotsLoading(false);
    }
  };

  const handleUploadActivity = async (shareToCoLearning = uploadPublic) => {
    if (!activityFile) {
      alert('Please select a file to upload');
      return;
    }

    if (!selectedArtwork) {
      alert('Please select an artwork first');
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', activityFile);
      formData.append('public', shareToCoLearning.toString());
      formData.append('uid', user.uid);
      formData.append('userName', user.displayName || user.email || 'Anonymous');
      formData.append('activityType', 'art-history-appreciation');
      formData.append('artworkId', selectedArtwork.id || selectedArtwork.imageUrl || 'unknown');
      formData.append('artworkTitle', selectedArtwork.title || 'Unknown Artwork');
      formData.append('artworkArtist', selectedArtwork.artistName || 'Unknown Artist');
      formData.append('artworkMovement', selectedArtwork.artMovement || '');
      formData.append('artworkImageUrl', selectedArtwork.imageUrl || '');

      // API_BASE already includes /api, so don't add it again
      const uploadEndpoint = API_BASE.endsWith('/api') 
        ? `${API_BASE}/upload-art-history-activity`
        : `${API_BASE}/api/upload-art-history-activity`;
      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setUploadSuccessData({
          shared: shareToCoLearning,
          artworkTitle: selectedArtwork.title || 'Unknown Artwork'
        });
        setShowSuccessPopup(true);
        setActivityFile(null);
        setUploadPublic(false);
        setShowShareConfirmation(false);
        // Auto-close success popup after 3 seconds
        setTimeout(() => {
          setShowSuccessPopup(false);
          if (shareToCoLearning) {
            // Navigate to co-learning space if shared
            navigate('/gallery/studio', { state: { activeTab: 'art-history' } });
          }
        }, 3000);
      } else {
        if (response.status === 404) {
          alert('Upload service is temporarily unavailable. Please try again later or contact support.');
          return;
        }
        let errMsg = 'Failed to upload activity';
        try {
          const error = await response.json();
          errMsg = error.error || errMsg;
        } catch (_) {}
        alert(errMsg);
      }
    } catch (error) {
      console.error('Error uploading activity:', error);
      alert('Error uploading activity');
    } finally {
      setIsUploading(false);
    }
  };

  // Fetch art movement description from Firebase when embedded in flow and we have result + artwork with movement
  useEffect(() => {
    const movementName = selectedArtwork?.artMovement;
    if (!location.state?.embeddedInFlow || !appreciationResult || !movementName) {
      setArtMovementData(null);
      return;
    }
    let cancelled = false;
    setArtMovementLoading(true);
    getArtMovementByName(movementName)
      .then((data) => {
        if (!cancelled) setArtMovementData(data || null);
      })
      .catch(() => {
        if (!cancelled) setArtMovementData(null);
      })
      .finally(() => {
        if (!cancelled) setArtMovementLoading(false);
      });
    return () => { cancelled = true; };
  }, [location.state?.embeddedInFlow, appreciationResult, selectedArtwork?.artMovement]);

  // When embedded and parent passes primaryArtwork, keep selectedArtwork in sync so resume always has artwork
  useEffect(() => {
    if (location.state?.embeddedInFlow !== true || !primaryArtworkProp) return;
    setSelectedArtwork((prev) => {
      const next = primaryArtworkProp;
      if (!next) return prev;
      if (prev?.id === next?.id && prev?.imageUrl === next?.imageUrl) return prev;
      return next;
    });
  }, [location.state?.embeddedInFlow, primaryArtworkProp?.id, primaryArtworkProp?.imageUrl]);

  // When parent has saved Step 1 result (e.g. user clicked Step 1 again), show it instantly without loader
  useEffect(() => {
    if (location.state?.embeddedInFlow !== true || !initialAppreciationResultProp) return;
    if (!(initialAppreciationResultProp.appreciation || initialAppreciationResultProp.points)) return;
    setAppreciationResult(initialAppreciationResultProp);
  }, [location.state?.embeddedInFlow, initialAppreciationResultProp?.appreciation?.length, initialAppreciationResultProp?.points?.length]);

  // Do NOT auto-load from cache when user has only selected an artwork.
  // Analysis view (hotspots + discovery panel) must show only after user clicks "Start analysis",
  // both for first-time and cached — same behaviour in both cases.
  // Reset panel + "user started analysis" flag when artwork or embedding context changes
  // Resuming a saved flow with Step 1 already done: do not reset viewer/flags — that caused empty state until cache fetch finished.
  useEffect(() => {
    if (step1AlreadyCompleteProp) return;
    setHasStartedAnalysis(false);
    setShowMovementStepsAfterDelay(false);
    setShowArtworkViewer(false);
    setSelectedOptionByPoint({});
    setRevealedByPoint({});
    setAnsweredHotspots({});
    setStep1QuizData(null);
  }, [displayArtworkId, embeddedInFlow, step1AlreadyCompleteProp]);

  // Step 1 quiz: NOT fetched when embedded — discovery hotspots drive the UI instead (quiz is unused, saves API cost)

  const retryLoadSavedAnalysis = useCallback(async () => {
    if (!displayArtwork || !embeddedInFlow) return;
    setHotspotsLoading(true);
    try {
      const existingHotspots = await fetchCachedHotspots(displayArtwork);
      if (existingHotspots && (existingHotspots.appreciation || existingHotspots.points)) {
        const finalResult = {
          appreciation: existingHotspots.appreciation,
          techniqueImprove: existingHotspots.techniqueImprove || [],
          activity: null,
          points: existingHotspots.points || [],
          suggestion: existingHotspots.suggestion || '',
          techniques: existingHotspots.artAnalysis?.techniques || [],
          artAnalysis: existingHotspots.artAnalysis || null,
          concepts: existingHotspots.concepts || [],
          agenticFormat: existingHotspots.agenticFormat || null,
          agenticAnalysis: existingHotspots.agenticAnalysis || null
        };
        setAppreciationResult(finalResult);
        if (embeddedInFlow) onAppreciationResult?.(finalResult);
      }
    } finally {
      setHotspotsLoading(false);
    }
  }, [displayArtwork, displayArtworkId, embeddedInFlow, onAppreciationResult]);

  // When embedded in a saved Learn Art History flow and Step 1 is already
  // marked complete (e.g. user resumes from Activity Dashboard), try once
  // to auto-load any cached analysis so the panel doesn't appear empty
  // until the user clicks "Retry loading analysis".
  const hasAutoLoadedSavedAnalysisRef = useRef(false);
  useEffect(() => {
    hasAutoLoadedSavedAnalysisRef.current = false;
  }, [displayArtworkId]);
  useEffect(() => {
    if (!embeddedInFlow) return;
    if (!step1AlreadyCompleteProp) return;
    if (!displayArtwork) return;
    if (appreciationResult) return;
    const initial = initialAppreciationResultProp;
    if (initial && (initial.appreciation || initial.points)) return;
    if (hasAutoLoadedSavedAnalysisRef.current) return;
    hasAutoLoadedSavedAnalysisRef.current = true;
    retryLoadSavedAnalysis();
  }, [embeddedInFlow, step1AlreadyCompleteProp, displayArtworkId, appreciationResult, retryLoadSavedAnalysis, initialAppreciationResultProp]);

  // When embedded with Step 1 already complete and we have appreciation
  // data (either from cache or a fresh run), automatically show the
  // artwork viewer + discovery panel instead of the "Complete the steps
  // above..." placeholder message.
  useEffect(() => {
    if (!embeddedInFlow) return;
    if (!step1AlreadyCompleteProp) return;
    if (!appreciationResult) return;
    setShowArtworkViewer(true);
  }, [embeddedInFlow, step1AlreadyCompleteProp, appreciationResult]);

  const handleFlowActiveIndexChange = (nextIndex) => {
    if (isDev) console.log('[Step1Flow] handleFlowActiveIndexChange:', nextIndex);
    setFlowActiveIndex(nextIndex);
    if (nextIndex == null) return;
    const key = `point-${nextIndex}`;
    // Ensure maps have entries for this point (but don't auto-mark as answered)
    setSelectedOptionByPoint((prev) => (key in prev ? prev : { ...prev, [key]: null }));
    setRevealedByPoint((prev) => (key in prev ? prev : { ...prev, [key]: false }));
  };

  const mainContent = (
    <>
    <div className={embeddedInFlow ? 'w-full flex-1 min-h-0 flex flex-col' : 'flex-1 min-h-0 flex flex-col'}>
        {/* Main Appreciation Layout - Only show when artwork is selected (or from flow prop on resume) */}
        {displayArtwork && (
          <div className={embeddedInFlow ? 'space-y-6' : 'grid grid-cols-1 lg:grid-cols-2 gap-8'}>
          {/* Left Column - Artwork Selection and Analysis (hidden when embedded; artwork already in focus above) */}
          {!embeddedInFlow && (
          <div className="space-y-6">
            {/* Artwork Selection */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-amber-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Select Artwork</h2>
              {selectedArtwork ? (
                <div className="space-y-4">
                  <div className="bg-amber-50 rounded-xl border border-amber-200 p-3 md:p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:space-x-4 gap-3 md:gap-0">
                      <img
                        src={resolveImageUrl(selectedArtwork.imageUrl)}
                        alt={selectedArtwork.title}
                        className="w-full h-32 md:w-20 md:h-20 object-cover rounded-lg border border-amber-200 md:flex-shrink-0"
                        data-src={selectedArtwork.imageUrl}
                        onError={(e) => {
                          const current = e.currentTarget.getAttribute('data-src') || e.currentTarget.src || selectedArtwork?.imageUrl || '';
                          if (current && !current.includes('/proxy-image?') && /^https?:\/\//i.test(current)) {
                            e.currentTarget.src = `${API_ENDPOINTS.backendBase}/proxy-image?url=${encodeURIComponent(current)}`;
                          }
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-base md:text-lg mb-1 break-words">{selectedArtwork.title}</h3>
                        <p className="text-sm md:text-base text-gray-600 mb-2">{selectedArtwork.artistName}</p>
                        <div className="mb-2 md:mb-0">
                          <ClickableArtMovement 
                            artMovement={selectedArtwork.artMovement} 
                            className="text-xs md:text-sm"
                            onPopupStateChange={setIsArtMovementPopupOpen}
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedArtwork(null)}
                        className="w-full md:w-auto px-4 py-2 md:py-1.5 text-sm md:text-base font-medium text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors border border-red-300 md:border-0 md:bg-transparent whitespace-nowrap"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => handleAnalyzeWithArtwork(selectedArtwork)}
                    disabled={isAnalyzing}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAnalyzing ? 'Analyzing...' : 'Start analysis'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowModal(true)}
                  className="w-full border-2 border-dashed border-amber-300 rounded-xl p-8 text-center hover:border-amber-400 hover:bg-amber-50 transition-colors"
                >
                  <div className="text-amber-600 mb-2">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <p className="text-amber-700 font-medium">Click to select an artwork</p>
                </button>
              )}
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              </div>
            )}

            {/* Loading States */}
            {hotspotsLoading && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-blue-700">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  Loading artwork details...
                </div>
              </div>
            )}

            {analysisLoading && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                  Generating appreciation analysis...
                </div>
              </div>
            )}
          </div>
          )}

          {/* When embedded: Analyze button only when no result yet, Step 1 not already complete, and user hasn't started (so it doesn't reappear after loader/movement steps) */}
          {embeddedInFlow && !appreciationResult && !step1AlreadyCompleteProp && !hasStartedAnalysis && (
            <div className="w-full px-4 py-3 md:px-6">
              <button
                type="button"
                onClick={() => handleAnalyzeWithArtwork(displayArtwork)}
                disabled={isAnalyzing}
                className="mx-auto block w-full max-w-md rounded-full border border-[#f5d99b] bg-gradient-to-r from-[#fde68a] to-[#fbbf24] py-3 px-6 text-sm font-semibold text-[#2F251B] shadow-md transition hover:from-[#fcd34d] hover:to-[#f59e0b] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isAnalyzing ? 'Analyzing...' : 'Start analysis'}
              </button>
              {error && (
                <div className="mt-2 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {error}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* When embedded and Step 1 already complete (saved flow): show "from saved flow" and loading until analysis is restored */}
          {embeddedInFlow && !appreciationResult && step1AlreadyCompleteProp && (
            <div className="w-full py-4 px-4 rounded-xl bg-amber-50 border-2 border-amber-200 text-center">
              <p className="text-amber-800 font-medium mb-2">This artwork analysis is from your saved flow (already completed).</p>
              {(hotspotsLoading || analysisLoading) ? (
                <div className="flex items-center justify-center gap-2 text-amber-700">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <span>Loading your saved analysis…</span>
                </div>
              ) : (
                <>
                  <p className="text-amber-700 text-sm mb-3">If analysis doesn’t appear shortly, try retrying or use &quot;Continue to remaining flow&quot; below to proceed.</p>
                  <button
                    type="button"
                    onClick={retryLoadSavedAnalysis}
                    className="py-2 px-4 rounded-xl text-sm font-semibold bg-amber-200 text-amber-900 border border-amber-300 hover:bg-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
                  >
                    Retry loading analysis
                  </button>
                </>
              )}
            </div>
          )}

          {/* Loading overlay — same style for both phases: (1) Start analysis clicked, (2) Preparing artwork learning / discovery */}
          {embeddedInFlow && !step1AlreadyCompleteProp && hasStartedAnalysis && (showLoaderOverlay || (showArtworkViewer && !discoveryHotspots?.length && discoveryHotspotsLoading)) && (
            <div className="fixed inset-0 flex items-center justify-center z-[9999] backdrop-blur-xl bg-white/10">
              <div className="bg-white rounded-xl shadow-2xl px-8 py-8 flex flex-col items-center mx-4 border-4 border-amber-300">
                <svg className="animate-spin h-12 w-12 text-amber-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
                {showLoaderOverlay ? (
                  <>
                    <div className="text-amber-700 font-bold text-lg text-center mb-2">
                      Amazing selection!
                    </div>
                    <div className="text-amber-800 text-base text-center font-semibold mb-1">
                      Artwork category:{' '}
                      <span className="text-amber-900">&quot;{loaderArtworkCategory}&quot;</span>
                    </div>
                    <div className="text-amber-600 text-sm text-center">
                      Let&apos;s see what story Artmosphere has to tell for &quot;{loaderArtworkName}&quot;.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-amber-700 font-bold text-lg text-center mb-2">Preparing your artwork learning...</div>
                    <div className="text-amber-600 text-sm text-center">Discovery is almost ready</div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Right Column - Results (full width when embedded) */}
          <div className={embeddedInFlow ? 'flex flex-col flex-1 min-h-0 gap-4' : 'space-y-6'}>
            {/* After 15s: show art movement steps from Firebase while backend jobs run */}
            {embeddedInFlow && hasStartedAnalysis && showMovementStepsAfterDelay && displayArtwork?.artMovement && !showArtworkViewer && (() => {
              const movementName = displayArtwork.artMovement;
              // Must match resolveArtformDocumentKey used for preload
              const artformKey = resolveArtformDocumentKey(displayArtwork);
              return (
                <ArtformAboutStepper
                  artformKey={artformKey}
                  onClose={null}
                  artistName={displayArtwork.artistName}
                  artworkTitle={displayArtwork.title}
                  movementName={displayArtwork.artMovement}
                  artworkImageUrl={displayArtwork.imageUrl}
                  onComplete={() => setShowArtworkViewer(true)}
                />
              );
            })()}

            {/* User completed movement steps; discovery loading uses same full-screen overlay above (no inline box) */}

            {(appreciationResult || (embeddedInFlow && showArtworkViewer && discoveryHotspots?.length > 0)) && (
              <>
                {/* When embedded: 2-column layout (desktop) — artwork hero left, Discovery Panel right; extends to near Mark as Done */}
                {embeddedInFlow && displayArtwork && (appreciationResult || discoveryHotspots?.length > 0) && (() => {
                  const artwork = displayArtwork;
                  
                  if (isDev) console.log('[Step1Flow] step1QuizData:', { exists: !!step1QuizData, pointsLength: step1QuizData?.points?.length });
                  const useStep1Quiz = !!step1QuizData && Array.isArray(step1QuizData.points) && step1QuizData.points.length === 4;
                  /** Embedded Step 1 uses discovery hotspots only (tap pins → cards); no multiple-choice quiz. */
                  const maxDiscoveryPins =
                    embeddedInFlow && Array.isArray(discoveryHotspots) && discoveryHotspots.length > 0
                      ? Math.min(4, discoveryHotspots.length)
                      : 0;

                  const flowPoints = useStep1Quiz && step1QuizData.points
                    ? step1QuizData.points.map(p => ({
                        id: p.id,
                        question: p.question || '',
                        options: Array.isArray(p.options) ? p.options : [],
                        explanation: p.explanation || p.whyThisWorks || '',
                        techniqueLearned: p.techniqueLearned || '',
                        afterChoiceLine: p.afterChoiceLine || "Nice thinking — let's see what works best for this effect.",
                        correctRevealLine: p.correctRevealLine || p.bestRevealLine || '',
                        bestRevealLine: p.bestRevealLine || p.correctRevealLine || '',
                        techniqueName: p.techniqueName || '',
                        whyThisWorks: p.whyThisWorks || p.explanation || '',
                        howToTry: Array.isArray(p.howToTry) ? p.howToTry : [],
                        tip: p.tip || ''
                      }))
                    : [];
                  
                  // Create string array for ArtAnalysisHotspots overlay (prefer discovery hotspots when available)
                  const hotspotTextArray = useStep1Quiz && step1QuizData.points
                    ? step1QuizData.points.map((p, idx) => {
                        const q = p.question || '';
                        const exp = p.explanation || '';
                        return `${q} ${exp}`.trim() || `Discovery point ${idx + 1}`;
                      })
                    : (discoveryHotspots && discoveryHotspots.length > 0)
                      ? discoveryHotspots.map(h => h.discoveryCard?.title || h.element || h.area || h.id)
                      : (appreciationResult?.appreciation && Array.isArray(appreciationResult.appreciation)
                        ? appreciationResult.appreciation.slice(0, 4)
                        : (appreciationResult?.points && Array.isArray(appreciationResult.points))
                          ? appreciationResult.points.slice(0, 4).map(p => (typeof p === 'string' ? p : (p?.text || p?.label || '')))
                          : []);
                  
                  const flowConcepts = appreciationResult?.concepts || [];

                  const totalPoints = useStep1Quiz ? flowPoints.length : maxDiscoveryPins;
                  if (isDev) {
                    console.log(
                      '[Step1Flow] discovery pins (max 4):',
                      maxDiscoveryPins,
                      'legacy quiz points:',
                      flowPoints.length,
                      'flowActiveIndex:',
                      flowActiveIndex
                    );
                  }
                  const effectiveIndex = (() => {
                    if (flowActiveIndex == null) return null;
                    if (useStep1Quiz && flowPoints[flowActiveIndex]) return flowActiveIndex;
                    if (!useStep1Quiz && maxDiscoveryPins > 0 && flowActiveIndex >= 0 && flowActiveIndex < maxDiscoveryPins) {
                      return flowActiveIndex;
                    }
                    return null;
                  })();

                  const currentKey = effectiveIndex != null ? `point-${effectiveIndex}` : null;
                  const selectedIndex = currentKey && Object.prototype.hasOwnProperty.call(selectedOptionByPoint, currentKey)
                    ? selectedOptionByPoint[currentKey]
                    : null;
                  const isRevealed = currentKey ? !!revealedByPoint[currentKey] : false;

                  const answeredCount = Object.keys(answeredHotspots).length;

                  const answeredMap = {};
                  for (let i = 0; i < totalPoints; i += 1) {
                    const key = `point-${i}`;
                    answeredMap[i] = !!answeredHotspots[key];
                  }

                  const buildHotspot = (idx) => {
                    if (!useStep1Quiz) return null;
                    if (idx == null || !flowPoints[idx]) return null;
                    const rawPoint = flowPoints[idx];
                    
                    // Legacy Step 1 quiz payload only (product no longer ships quiz in embedded flow)
                    if (rawPoint.options && Array.isArray(rawPoint.options)) {
                      const options = rawPoint.options.map(opt => (typeof opt === 'object' && opt != null ? opt.text : opt));
                      const bestIdx = rawPoint.options.findIndex(opt => opt && (opt.isBest === true || opt.isCorrect === true));
                      const correctIndex = bestIdx >= 0 ? bestIdx : 0;
                      const bestText = options[correctIndex] || '';
                      let safeQuestion = rawPoint.question || '';
                      if (typeof safeQuestion === 'string') {
                        safeQuestion = safeQuestion.trim();
                        const lowerQ = safeQuestion.toLowerCase();
                        if (lowerQ.startsWith('point ') || lowerQ.includes('point ') || lowerQ.includes('used here')) {
                          safeQuestion = 'Look at this part of the artwork. If you wanted to create a similar effect in this style, what would you do?';
                        }
                      }
                      return {
                        number: idx + 1,
                        question: safeQuestion,
                        options,
                        correctIndex,
                        bestIndex: correctIndex,
                        afterChoiceLine: rawPoint.afterChoiceLine || "Nice thinking — let's see what works best for this effect.",
                        correctRevealLine: rawPoint.correctRevealLine || rawPoint.bestRevealLine || (bestText ? `Best choice for this effect: ${bestText}.` : 'Best choice for this effect.'),
                        bestRevealLine: rawPoint.bestRevealLine || rawPoint.correctRevealLine || (bestText ? `Best choice for this effect: ${bestText}.` : 'Best choice for this effect.'),
                        explanation: rawPoint.explanation || rawPoint.whyThisWorks || '',
                        whyThisWorks: rawPoint.whyThisWorks || rawPoint.explanation || '',
                        techniqueName: rawPoint.techniqueName || '',
                        techniqueLearned: rawPoint.techniqueLearned || '',
                        howToTry: Array.isArray(rawPoint.howToTry) ? rawPoint.howToTry : [],
                        tip: rawPoint.tip || ''
                      };
                    }
                    
                    // Fallback to old format
                    const concept = flowConcepts[idx] || {};
                    const fullText = typeof rawPoint === 'string'
                      ? rawPoint
                      : (rawPoint?.text || rawPoint?.description || '');

                    const sentences = (fullText || '')
                      .split(/(?<=[.!?])\s+/)
                      .map(s => s.trim())
                      .filter(Boolean);

                    const question =
                      concept.question ||
                      concept.promptQuestion ||
                      concept.prompt ||
                      (sentences[0] || 'What do you notice first here?');

                    const explanation =
                      concept.explanation ||
                      concept.answer ||
                      sentences.slice(1).join(' ');

                    let options = Array.isArray(concept.options) ? concept.options : [];
                    if (!options.length && rawPoint && Array.isArray(rawPoint.options)) {
                      options = rawPoint.options;
                    }
                    if (!options.length) {
                      options = ['Lines', 'Shapes', 'Light'];
                    }
                    options = options.slice(0, 3);
                    while (options.length < 3) {
                      options.push('More detail');
                    }

                    let correctIndex = typeof concept.correctIndex === 'number'
                      ? concept.correctIndex
                      : (typeof rawPoint?.correctIndex === 'number' ? rawPoint.correctIndex : 0);
                    if (correctIndex < 0 || correctIndex >= options.length) {
                      correctIndex = 0;
                    }

                    return {
                      number: idx + 1,
                      question,
                      options,
                      correctIndex,
                      afterChoiceLine: "That's an interesting choice!",
                      correctRevealLine: `The best answer is: ${options[correctIndex]}.`,
                      explanation,
                      techniqueLearned: concept.techniqueLearned || concept.technique || '',
                    };
                  };

                  const currentHotspot = buildHotspot(effectiveIndex);

                  const goNextPoint = () => {
                    if (totalPoints === 0) return;
                    const next = effectiveIndex == null ? 0 : Math.min(effectiveIndex + 1, totalPoints - 1);
                    handleFlowActiveIndexChange(next);
                  };

                  const handleOptionSelect = (optionIndex) => {
                    if (!currentKey || !currentHotspot) return;

                    const correctIndex = currentHotspot.correctIndex ?? 0;
                    const isCorrect = optionIndex === correctIndex;

                    // Immediately reveal when option is selected
                    setSelectedOptionByPoint((prev) => ({
                      ...prev,
                      [currentKey]: optionIndex,
                    }));
                    setRevealedByPoint((prev) => ({
                      ...prev,
                      [currentKey]: true,
                    }));
                    setAnsweredHotspots((prev) => ({
                      ...prev,
                      [currentKey]: { selectedIndex: optionIndex, isCorrect },
                    }));
                  };

                  const handleNextFromPanel = () => {
                    if (!currentKey) return;
                    goNextPoint();
                  };

                  return (
                    <div className="flex flex-col gap-4 md:gap-6 flex-1 min-h-0">
                      {/* Show About Box OR Artwork Viewer - Not Both */}
                      {!showArtworkViewer ? (
                        /* When embedded, stepper is already shown above — avoid double window; show short message instead */
                        embeddedInFlow && artwork.artMovement ? (
                          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 text-center">
                            <p className="text-amber-800 font-medium">Complete the steps above to explore this artwork.</p>
                          </div>
                        ) : artwork.artMovement && (
                          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-amber-200 shadow-lg">
                            {(() => {
                              const movementName = artwork.artMovement;
                              const artformKey = resolveArtformDocumentKey(artwork);

                              return (
                                <ArtformAboutStepper
                                  artformKey={artformKey}
                                  onClose={null}
                                  artistName={artwork.artistName}
                                  artworkTitle={artwork.title}
                                  movementName={artwork.artMovement}
                                  artworkImageUrl={artwork.imageUrl}
                                  onComplete={() => setShowArtworkViewer(true)}
                                />
                              );
                            })()}
                          </div>
                        )
                      ) : (
                        /* New Container: Artwork Viewer and Discovery Panel */
                        <div className="flex flex-col gap-4 md:gap-6 flex-1 min-h-0">
                          {/* Artwork Information Header */}
                          <div className="px-4 flex-shrink-0">
                            <div className="bg-white rounded-xl p-4 md:p-5 border border-gray-200 shadow-sm">
                              <p className="text-base md:text-lg text-black font-bold mb-2">
                                {artwork.title} by {artwork.artistName}
                              </p>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs md:text-sm text-gray-600">Style:</span>
                                <span className="text-sm md:text-base font-semibold text-black">
                                  {artwork.artMovement || 'Art Movement'}
                                </span>
                                {artwork.timePeriod && (
                                  <span className="text-xs text-gray-500">({artwork.timePeriod})</span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.8fr)] gap-4 md:gap-6 flex-1 min-h-0 items-stretch flex-shrink-0">
                            {/* LEFT: Artwork Viewer */}
                            <div className="flex flex-col min-w-0 min-h-0">
                              <div className="flex-1 min-h-0 rounded-xl md:rounded-2xl overflow-hidden border-2 md:border-4 border-amber-200 shadow-xl bg-amber-50/80 flex flex-col h-full min-h-[45vh] sm:min-h-[55vh]">
                                {(() => {
                                  // Prefer new discovery hotspots for overlay pins; fallback to v2 elements, then legacy hotspots
                                  const agenticData = appreciationResult?.agenticFormat || appreciationResult?.agenticAnalysis;
                                  const v2Elements = agenticData?.discover?.elements;
                                  let imageOverlayText;
                                  if (discoveryHotspots && discoveryHotspots.length > 0) {
                                    const pinHotspots = embeddedInFlow
                                      ? discoveryHotspots.slice(0, 4)
                                      : discoveryHotspots;
                                    imageOverlayText = pinHotspots.map(h => h.discoveryCard?.title || h.element || h.area || h.id);
                                  } else if (v2Elements && v2Elements.length > 0) {
                                    imageOverlayText = v2Elements.map(el => `${el.label} - ${el.whereToLook}`);
                                  } else {
                                    imageOverlayText = hotspotTextArray;
                                  }

                                  return (
                                    <ArtAnalysisHotspots
                                      imageUrl={artwork.imageUrl}
                                      analysisText={imageOverlayText}
                                      title={artwork.title}
                                      enableFullscreen={true}
                                      layout="flow"
                                      concepts={flowConcepts}
                                      hideHotspots={false}
                                      activeIndex={flowActiveIndex}
                                      onActivePointChange={(index) => {
                                        handleFlowActiveIndexChange(index);
                                        // Sync selected discovery hotspot card when pins are based on discoveryHotspots
                                        if (discoveryHotspots && discoveryHotspots.length > 0 && index != null && index >= 0) {
                                          const list = embeddedInFlow ? discoveryHotspots.slice(0, 4) : discoveryHotspots;
                                          if (index < list.length) {
                                            const h = list[index];
                                            setSelectedDiscoveryHotspotId(h?.id || null);
                                          }
                                        }
                                        if (isDev) console.log('[ImageOverlay] Pin clicked:', index);
                                      }}
                                      activeSection={flowActiveSection}
                                      onSectionChange={(s) => { setFlowActiveSection(s); if (s === 'concepts') setFlowActiveIndex(null); }}
                                    />
                                  );
                                })()}
                              </div>
                            </div>

                            {/* RIGHT: Discovery Panel - ALWAYS use DiscoverHotspotsPanel */}
                            <div className="flex flex-col gap-3 min-h-0 flex-1">
                              <div className="rounded-xl md:rounded-2xl border-2 border-amber-200 shadow-lg p-4 flex flex-col flex-1 min-h-0 overflow-y-auto bg-gradient-to-br from-amber-50 via-amber-100/70 to-yellow-100">
                                {(() => {
                                  // In the guided Step 1 flow, we ONLY show the new
                                  // element-based discovery UI (no legacy 3-area shell).
                                  if (embeddedInFlow) {
                                    if (discoveryHotspotsLoading) {
                                      return (
                                        <div className="flex-1 flex items-center justify-center">
                                          <p className="text-amber-800 text-sm">
                                            Preparing discovery cards for this artwork…
                                          </p>
                                        </div>
                                      );
                                    }

                                    if (discoveryHotspots && discoveryHotspots.length > 0) {
                                      return (
                                        <div className="space-y-3" style={{ perspective: '1200px' }}>
                                          <h3 className="text-lg md:text-xl font-bold text-amber-900">
                                            Discover This Artwork
                                          </h3>
                                          <p className="text-xs md:text-sm text-amber-800">
                                            Tap a hotspot on the picture to flip the discovery card and learn about that part of the artwork.
                                          </p>
                                          <AnimatePresence mode="wait">
                                            {selectedDiscoveryHotspotId && (() => {
                                              const h = discoveryHotspots.find((x) => x.id === selectedDiscoveryHotspotId);
                                              if (!h?.discoveryCard) return null;
                                              const card = h.discoveryCard;
                                              return (
                                                <motion.div
                                                  key={h.id}
                                                  initial={{ rotateY: 90, opacity: 0 }}
                                                  animate={{ rotateY: 0, opacity: 1 }}
                                                  exit={{ rotateY: -90, opacity: 0 }}
                                                  transition={{ duration: 0.45, ease: 'easeInOut' }}
                                                  className="rounded-xl border border-amber-300 bg-white p-4 space-y-3 shadow-md"
                                                  style={{ transformStyle: 'preserve-3d' }}
                                                >
                                                  <h4 className="font-bold text-amber-900">{card.title}</h4>
                                                  <p className="text-sm text-amber-900 leading-relaxed">{card.appreciation}</p>
                                                  {card.techniques && card.techniques.length > 0 && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-amber-800 mb-2">Techniques</p>
                                                      <div className="flex flex-wrap gap-2">
                                                        {card.techniques.map((t, i) => (
                                                          <button
                                                            key={i}
                                                            type="button"
                                                            onClick={() => fetchTechniqueDrilldown(t.tapToLearnKey, artwork)}
                                                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200"
                                                          >
                                                            {formatTechniqueDisplayName(t.name)}
                                                          </button>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  )}
                                                </motion.div>
                                              );
                                            })()}
                                          </AnimatePresence>
                                          {!selectedDiscoveryHotspotId && (
                                            <p className="text-xs text-amber-700">
                                              Tap a hotspot on the artwork to reveal its discovery card.
                                            </p>
                                          )}
                                        </div>
                                      );
                                    }

                                    // Flow mode, but no discovery data available
                                    return (
                                      <div className="flex-1 flex items-center justify-center">
                                        <div className="text-center px-4">
                                          <p className="text-amber-800 text-sm font-medium mb-2">
                                            Discovery cards for this artwork could not be generated right now.
                                          </p>
                                          <p className="text-amber-700 text-xs">
                                            You can still explore the artwork using the questions on the left, or try again in a little while.
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  }

                                  // Non-flow entry points: keep the old agentic 3-area shell as a fallback
                                  // 100% RELIABLE: Ensure agenticFormat is always present
                                  let agenticData = null;
                                  let dataSource = null;
                                  
                                  // 1. First try: appreciationResult
                                  if (appreciationResult?.agenticFormat?.discover?.elements) {
                                    agenticData = appreciationResult.agenticFormat;
                                    dataSource = 'appreciationResult.agenticFormat';
                                  } else if (appreciationResult?.agenticAnalysis?.discover?.elements) {
                                    agenticData = appreciationResult.agenticAnalysis;
                                    dataSource = 'appreciationResult.agenticAnalysis';
                                  }
                                  
                                  // 2. Second try: Build from appreciationResult if it has data
                                  if (!agenticData?.discover?.elements && appreciationResult) {
                                    const ensured = ensureAgenticFormat(appreciationResult, artwork);
                                    if (ensured?.agenticFormat?.discover?.elements) {
                                      agenticData = ensured.agenticFormat;
                                      dataSource = 'built from appreciationResult';
                                      setAppreciationResult(ensured);
                                    } else if (ensured?.agenticAnalysis?.discover?.elements) {
                                      agenticData = ensured.agenticAnalysis;
                                      dataSource = 'built from appreciationResult (agenticAnalysis)';
                                      setAppreciationResult(ensured);
                                    }
                                  }
                                  
                                  // 3. Third try: Use cachedHotspots if available
                                  if (!agenticData?.discover?.elements && cachedHotspots) {
                                    if (cachedHotspots.agenticFormat?.discover?.elements) {
                                      agenticData = cachedHotspots.agenticFormat;
                                      dataSource = 'cachedHotspots.agenticFormat';
                                    } else if (cachedHotspots.agenticAnalysis?.discover?.elements) {
                                      agenticData = cachedHotspots.agenticAnalysis;
                                      dataSource = 'cachedHotspots.agenticAnalysis';
                                    } else {
                                      const ensured = ensureAgenticFormat(cachedHotspots, artwork);
                                      if (ensured?.agenticFormat?.discover?.elements) {
                                        agenticData = ensured.agenticFormat;
                                        dataSource = 'built from cachedHotspots';
                                        setCachedHotspots({ ...cachedHotspots, agenticFormat: agenticData, agenticAnalysis: agenticData });
                                        if (!appreciationResult) {
                                          setAppreciationResult(ensured);
                                        }
                                      }
                                    }
                                  }
                                  
                                  // 4. Last resort: Try to build from any available data
                                  if (!agenticData?.discover?.elements && artwork) {
                                    const fallbackData = {
                                      appreciation: appreciationResult?.appreciation || cachedHotspots?.appreciation || [],
                                      points: appreciationResult?.points || cachedHotspots?.points || []
                                    };
                                    if (fallbackData.appreciation.length > 0 || fallbackData.points.length > 0) {
                                      const ensured = ensureAgenticFormat(fallbackData, artwork);
                                      if (ensured?.agenticFormat?.discover?.elements) {
                                        agenticData = ensured.agenticFormat;
                                        dataSource = 'built from fallback data';
                                        if (!appreciationResult) {
                                          setAppreciationResult(ensured);
                                        }
                                      }
                                    }
                                  }
                                  
                                  if (isDev) {
                                    console.log('[DiscoverPanel] agentic v2 check (fallback shell):', { 
                                      hasAgentic: !!agenticData?.discover?.elements, 
                                      elementsCount: agenticData?.discover?.elements?.length ?? 0,
                                      dataSource: dataSource || 'none'
                                    });
                                  }
                                  
                                  if (agenticData && 
                                      agenticData.discover && 
                                      Array.isArray(agenticData.discover.elements) && 
                                      agenticData.discover.elements.length > 0) {
                                    return (
                                      <DiscoverElementsPanel
                                        analysis={agenticData}
                                        artworkImageUrl={artwork.imageUrl}
                                        activeElementIndex={flowActiveIndex}
                                        onElementClick={(elementId) => {
                                          if (isDev) console.log('Element clicked (fallback shell):', elementId);
                                          const elementIndex = agenticData.discover.elements.findIndex(el => el.id === elementId);
                                          if (elementIndex >= 0) {
                                            handleFlowActiveIndexChange(elementIndex);
                                          }
                                        }}
                                        onConceptClick={(conceptId) => {
                                          console.log('Concept clicked (fallback shell):', conceptId);
                                        }}
                                      />
                                    );
                                  }
                                  
                                  if (isDev) console.warn('[DiscoverPanel] No agentic v2 data available — tried all fallbacks');
                                  
                                  // Fallback: Show concepts if available
                                  if (flowActiveSection === 'concepts' && flowConcepts.length > 0) {
                                    return (
                                      <div className="overflow-y-auto flex-1 min-h-0 space-y-2 pr-1">
                                        {flowConcepts.map((c, i) => (
                                          <div key={i} className="bg-amber-50 rounded-lg p-2.5 border border-amber-200">
                                            <h4 className="font-semibold text-amber-900 text-sm">{c.title || 'Concept'}</h4>
                                            <p className="text-amber-800 text-xs leading-relaxed">{c.text || ''}</p>
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  }
                                  
                                  // Final fallback: Show helpful message instead of "Preparing discoveries..."
                                  return (
                                    <div className="flex-1 flex items-center justify-center">
                                      <div className="text-center px-4">
                                        <p className="text-amber-800 text-sm font-medium mb-2">
                                          {appreciationResult || cachedHotspots 
                                            ? 'Click "Start analysis" to generate discovery details for this artwork.'
                                            : 'Loading artwork data...'}
                                        </p>
                                        {isDev && (
                                          <p className="text-amber-600 text-xs mt-2">
                                            Debug: appreciationResult={!!appreciationResult}, cachedHotspots={!!cachedHotspots}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>

                          {/* Mark as Done Button - Show ONLY in artwork viewer container (Image 2) with hotspots */}
                          {showArtworkViewer && (onStep1Complete || location.state?.embeddedInFlow) && (
                            <div className="flex-shrink-0 px-4 md:px-6 py-3 mt-4 flex flex-col sm:flex-row items-center justify-center gap-2 border-t-2 border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-b-xl shadow-sm">
                              <span className="text-amber-800 text-sm font-medium text-center sm:text-left">
                                When you&apos;re done exploring this artwork, click the button to move to Step 2.
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  if (onStep1Complete) {
                                    onStep1Complete();
                                  } else if (location.state?.embeddedInFlow) {
                                    navigate('/learn-art-history-flow', { 
                                      state: { 
                                        ...location.state, 
                                        stepIndex: 1 
                                      } 
                                    });
                                  }
                                }}
                                className="py-2 px-5 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 flex-shrink-0 hover:shadow-lg transition-all duration-200"
                                style={{
                                  background: 'linear-gradient(180deg, #FFE082 0%, #FFD54F 100%)',
                                  boxShadow: '0 2px 8px rgba(255, 193, 7, 0.35)',
                                }}
                              >
                                Mark as Done
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* No separate CTA card when embedded — instruction is in Mark as Done bar (LearnArtHistoryFlow) to save space */}

                {/* Activity — only when not in Learn Art History flow (activity is in Step 3) */}
                {!embeddedInFlow && appreciationResult.activity && (
                  <div className="bg-white rounded-2xl shadow-lg p-6 border border-amber-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900">Try This Activity</h3>
                      <button
                        onClick={() => setVisibleSection(visibleSection === 'activity' ? null : 'activity')}
                        className="text-amber-600 hover:text-amber-700 font-medium"
                      >
                        {visibleSection === 'activity' ? 'Hide' : 'Show'} Details
                      </button>
                    </div>
                    {visibleSection === 'activity' && (
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">{appreciationResult.activity.title}</h4>
                          <p className="text-gray-700 mb-3">{appreciationResult.activity.objective}</p>
                        </div>
                        
                        {appreciationResult.activity.materials && (
                          <div>
                            <h5 className="font-medium text-gray-900 mb-2">Materials:</h5>
                            <ul className="list-disc list-inside text-gray-700 space-y-1">
                              {appreciationResult.activity.materials.map((material, index) => (
                                <li key={index}>{material}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {appreciationResult.activity.steps && (
                          <div>
                            <h5 className="font-medium text-gray-900 mb-2">Steps:</h5>
                            <div 
                              className="overflow-y-auto flex-1 min-h-0"
                              style={{ 
                                maxHeight: typeof window !== 'undefined' && window.innerWidth < 768 ? 'calc(100vh - 50vh)' : '400px',
                                WebkitOverflowScrolling: 'touch',
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#f97316 #fef3c7',
                                scrollPaddingTop: typeof window !== 'undefined' && window.innerWidth < 768 ? '1rem' : '0',
                                scrollPaddingBottom: '1rem',
                                overscrollBehavior: 'contain'
                              }}
                            >
                              <style>{`
                                div[style*="maxHeight"]::-webkit-scrollbar {
                                  width: 8px;
                                }
                                div[style*="maxHeight"]::-webkit-scrollbar-track {
                                  background: #fef3c7;
                                  border-radius: 4px;
                                }
                                div[style*="maxHeight"]::-webkit-scrollbar-thumb {
                                  background: #f97316;
                                  border-radius: 4px;
                                }
                                div[style*="maxHeight"]::-webkit-scrollbar-thumb:hover {
                                  background: #ea580c;
                                }
                              `}</style>
                              <div className="space-y-2 pr-2">
                                {appreciationResult.activity.steps.map((step, index) => {
                                  const isExpanded = expandedActivitySteps[index];
                                  // Truncate step text for preview (show first 60 characters for smaller display)
                                  const previewText = step.length > 60 ? step.substring(0, 60) + '...' : step;
                                  return (
                                    <div key={index} className="border border-amber-200 rounded-lg overflow-hidden bg-white">
                                      <button
                                        onClick={() => setExpandedActivitySteps(prev => ({
                                          ...prev,
                                          [index]: !prev[index]
                                        }))}
                                        className="w-full px-3 py-2 text-left flex items-center justify-between hover:bg-amber-50 transition-colors"
                                      >
                                        <div className="flex items-start gap-2 flex-1">
                                          <span className="bg-amber-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                                            {index + 1}
                                          </span>
                                          <span className="text-gray-800 text-sm flex-1 leading-relaxed break-words">
                                            {isExpanded ? step : previewText}
                                          </span>
                                        </div>
                                        <svg
                                          className={`w-4 h-4 text-amber-600 flex-shrink-0 ml-2 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </button>
                                      {isExpanded && (
                                        <div className="px-3 pb-2 pt-1 bg-amber-50 border-t border-amber-200">
                                          <div className="pl-8">
                                            <p className="text-xs text-amber-600 italic">
                                              💡 Tip: Take your time with this step and refer back to the artwork for inspiration.
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Activity Upload — only when not in flow (activity is in Step 3) */}
                {!embeddedInFlow && (
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-amber-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Share Your Activity</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Upload your activity result
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setActivityFile(e.target.files[0])}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="public"
                        checked={uploadPublic}
                        onChange={(e) => setUploadPublic(e.target.checked)}
                        className="rounded"
                      />
                      <label htmlFor="public" className="text-sm text-gray-700">
                        Make this public (visible to other users)
                      </label>
                    </div>
                    <button
                      onClick={() => setShowShareConfirmation(true)}
                      disabled={!activityFile}
                      className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Upload Activity Artwork
                    </button>
                  </div>
                </div>
                )}
              </>
            )}
          </div>
        </div>
        )}

      {/* Share confirmation for activity upload */}
      {showShareConfirmation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9998] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border-4 border-amber-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Share to Art History Learning Space?</h3>
            <p className="text-gray-700 mb-6">
              Would you like to share your activity artwork to the Art History Learning Space so others can see your interpretation?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => handleUploadActivity(false)}
                disabled={isUploading}
                className="flex-1 bg-gray-200 text-gray-800 font-semibold py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Upload Only
              </button>
              <button
                onClick={() => handleUploadActivity(true)}
                disabled={isUploading}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-3 px-4 rounded-lg hover:from-amber-600 hover:to-orange-600 transition-colors disabled:opacity-50"
              >
                Share to Co-Learning
              </button>
            </div>
            <button
              onClick={() => setShowShareConfirmation(false)}
              disabled={isUploading}
              className="w-full mt-3 text-gray-600 hover:text-gray-800 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

        {/* Custom Artwork Selection and Analysis Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[10000] p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {!showAnalysis ? 'Choose Artwork' : 'Artwork Analysis'}
                  </h3>
                  <button
                      onClick={() => {
                        setShowModal(false);
                        setShowAnalysis(false);
                        setSelectedArtwork(null);
                        setAppreciationResult(null);
                        if (isFromMentorDashboard) {
                          navigate('/mentor-dashboard');
                        } else {
                          window.history.back();
                        }
                      }}
                      className="text-white/90 hover:text-white text-3xl leading-none transition-colors duration-200"
                    >
                      ×
                    </button>
                </div>
              </div>

              {/* Loading Overlay — amber only (no grey) */}
              {(isAnalyzing || analysisLoading) && (
                <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-amber-200/60">
                  <div className="bg-white rounded-xl shadow-2xl px-8 py-8 flex flex-col items-center mx-4 border-4 border-amber-300">
                    <svg className="animate-spin h-12 w-12 text-amber-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    <div className="text-amber-700 font-bold text-lg text-center mb-2">
                      Amazing selection!
                    </div>
                    <div className="text-amber-800 text-base text-center font-semibold mb-1">
                      Artwork category:{' '}
                      <span className="text-amber-900">&quot;{loaderArtworkCategory}&quot;</span>
                    </div>
                    <div className="text-amber-600 text-sm text-center">
                      Let&apos;s see what story Artmosphere has to tell for &quot;{loaderArtworkName}&quot;.
                    </div>
                  </div>
                </div>
              )}

              {/* Modal Content */}
              <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
                {!showAnalysis ? (
                  /* Artwork Gallery Interface */
                  <div className="space-y-6">
                    {/* Filter Section */}
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Artworks</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Art Movement</label>
                          <select
                            value={filters.artMovement}
                            onChange={(e) => setFilters(prev => ({ ...prev, artMovement: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          >
                            <option value="">All Movements</option>
                            {artMovements.map(movement => (
                              <option key={movement} value={movement}>{movement}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Art Form</label>
                          <select
                            value={filters.artForm}
                            onChange={(e) => setFilters(prev => ({ ...prev, artForm: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          >
                            <option value="">All Forms</option>
                            {artForms.map(form => (
                              <option key={form} value={form}>{form}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                          <select
                            value={filters.typeOfArtwork}
                            onChange={(e) => setFilters(prev => ({ ...prev, typeOfArtwork: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          >
                            <option value="">All Types</option>
                            {artworkTypes.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Time Period</label>
                          <input
                            type="text"
                            value={filters.timePeriod}
                            onChange={(e) => setFilters(prev => ({ ...prev, timePeriod: e.target.value }))}
                            placeholder="e.g., Renaissance"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button
                          onClick={() => setFilters({
                            artMovement: '',
                            artCategory: '',
                            artForm: '',
                            timePeriod: '',
                            typeOfArtwork: ''
                          })}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                        >
                          Clear Filters
                        </button>
                        <span className="text-sm text-gray-600 flex items-center">
                          {filteredArtworks.length} artworks found
                        </span>
                      </div>
                    </div>

                    {/* Thumbnail Gallery */}
                    {filteredArtworks.length > 0 && (
                      <div className="bg-white rounded-xl p-4 border border-amber-200 shadow-lg">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Select Artwork</h4>
                        <div className="flex gap-3 overflow-x-auto pb-2" style={{ maxHeight: '120px' }}>
                          {filteredArtworks.map((artwork, index) => (
                            <button
                              key={artwork.id}
                              onClick={() => setCurrentIndex(index)}
                              className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                                index === currentIndex 
                                  ? 'border-amber-500 shadow-lg scale-105' 
                                  : 'border-gray-200 hover:border-amber-300 hover:shadow-md'
                              }`}
                              style={{ width: '80px', height: '80px' }}
                            >
                              <img
                                src={resolveImageUrl(artwork.imageUrl)}
                                alt={artwork.title}
                                className="w-full h-full object-cover"
                                data-src={artwork.imageUrl}
                                onError={(e) => {
                                  const current = e.currentTarget.getAttribute('data-src') || e.currentTarget.src || artwork?.imageUrl || '';
                                  if (current && !current.includes('/proxy-image?') && /^https?:\/\//i.test(current)) {
                                    e.currentTarget.src = `${API_ENDPOINTS.backendBase}/proxy-image?url=${encodeURIComponent(current)}`;
                                  } else {
                                    e.target.style.display = 'none';
                                  }
                                }}
                              />
                            </button>
                          ))}
                        </div>
                        <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
                          <span>{currentIndex + 1} of {filteredArtworks.length} artworks</span>
                          <div className="flex gap-2">
                            <button 
                              onClick={prevArtwork} 
                              className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              disabled={currentIndex === 0}
                            >
                              ←
                            </button>
                            <button 
                              onClick={nextArtwork} 
                              className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              disabled={currentIndex === filteredArtworks.length - 1}
                            >
                              →
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Artwork Display */}
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading artworks...</p>
                      </div>
                    ) : filteredArtworks.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500 mb-2">No artworks found matching your filters.</p>
                        <p className="text-sm text-gray-400">Try adjusting your filter criteria.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Current Artwork Display */}
                        <div className="bg-white rounded-2xl shadow-lg p-6 border border-amber-200">
                          
                          {filteredArtworks[currentIndex] && (
                            <>
                              {/* Normal Artwork Display */}
                              <div className="space-y-4">
                                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-6 border-4 border-amber-200 shadow-xl relative">
                                  {/* Expand Button - Top Right Corner */}
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('[Appreciation] Expand button clicked, current state:', isExpanded);
                                      setIsExpanded(!isExpanded);
                                    }}
                                    className="absolute bottom-4 right-4 p-3 bg-white hover:bg-gray-50 rounded-xl shadow-xl transition-all duration-200 border-2 border-gray-300 hover:border-amber-400"
                                    title={isExpanded ? 'Shrink Artwork' : 'Expand Artwork'}
                                    style={{ 
                                      minWidth: '44px', 
                                      minHeight: '44px',
                                      zIndex: 99999,
                                      isolation: 'isolate'
                                    }}
                                  >
                                    {isExpanded ? (
                                      <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15.586 13H14a1 1 0 010-2h4z" clipRule="evenodd" />
                                      </svg>
                                    ) : (
                                      <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15.586 13H14a1 1 0 010-2h4z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </button>
                                  
                                  <div className="flex items-center justify-center overflow-hidden" style={{ height: '500px', maxWidth: '100%' }}>
                                    <img
                                      key={`${filteredArtworks[currentIndex].id}-${currentIndex}`}
                                      src={resolveImageUrl(filteredArtworks[currentIndex].imageUrl)}
                                      alt={filteredArtworks[currentIndex].title}
                                      className="max-w-full max-h-full object-contain rounded-xl shadow-lg"
                                      style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }}
                                      data-src={filteredArtworks[currentIndex].imageUrl}
                                      onError={(e) => {
                                        const current = e.currentTarget.getAttribute('data-src') || e.currentTarget.src || filteredArtworks[currentIndex]?.imageUrl || '';
                                        if (current && !current.includes('/proxy-image?') && /^https?:\/\//i.test(current)) {
                                          e.currentTarget.src = `${API_ENDPOINTS.backendBase}/proxy-image?url=${encodeURIComponent(current)}`;
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Pop-out Artwork Overlay */}
                              {isExpanded && (
                                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[10001] p-8">
                                  <div className="relative max-w-[90vw] max-h-[90vh] bg-white rounded-2xl shadow-2xl border-4 border-amber-200 overflow-hidden">
                                    {/* Close Button for Pop-out - Top Right - Above Key Concepts - Small and Aesthetic */}
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('[Appreciation] Close pop-out button clicked');
                                        setIsExpanded(false);
                                      }}
                                      className="absolute top-3 right-3 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all duration-200 border border-gray-200 hover:border-amber-400 hover:shadow-lg"
                                      title="Close"
                                      style={{ 
                                        width: '28px', 
                                        height: '28px',
                                        zIndex: 99999,
                                        isolation: 'isolate',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <svg className="w-3.5 h-3.5 text-gray-600 hover:text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                    
                                    <div className="flex items-center justify-center overflow-hidden" style={{ height: '80vh', maxWidth: '100%' }}>
                                      <img
                                        key={`popout-${filteredArtworks[currentIndex].id}-${currentIndex}`}
                                        src={resolveImageUrl(filteredArtworks[currentIndex].imageUrl)}
                                        alt={filteredArtworks[currentIndex].title}
                                        className="max-w-full max-h-full object-contain rounded-xl shadow-lg"
                                        style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }}
                                        data-src={filteredArtworks[currentIndex].imageUrl}
                                        onError={(e) => {
                                          const current = e.currentTarget.getAttribute('data-src') || e.currentTarget.src || filteredArtworks[currentIndex]?.imageUrl || '';
                                          if (current && !current.includes('/proxy-image?') && /^https?:\/\//i.test(current)) {
                                            e.currentTarget.src = `${API_ENDPOINTS.backendBase}/proxy-image?url=${encodeURIComponent(current)}`;
                                          }
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {filteredArtworks[currentIndex] && (
                            <div className="space-y-2">
                              <h3 className="text-xl font-bold text-gray-900">{filteredArtworks[currentIndex].title}</h3>
                              <p className="text-gray-600">{filteredArtworks[currentIndex].artistName}</p>
                              <div className="flex flex-wrap gap-2">
                                {filteredArtworks[currentIndex].artMovement && (
                                  <ClickableArtMovement 
                                    artMovement={filteredArtworks[currentIndex].artMovement} 
                                    className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm"
                                    onPopupStateChange={setIsArtMovementPopupOpen}
                                  />
                                )}
                                {filteredArtworks[currentIndex].timePeriod && (
                                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                    {filteredArtworks[currentIndex].timePeriod}
                                  </span>
                                )}
                                {filteredArtworks[currentIndex].typeOfArtwork && (
                                  <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                                    {filteredArtworks[currentIndex].typeOfArtwork}
                                  </span>
                                )}
                              </div>
                              
                              <button
                                onClick={async () => {
                                  console.log('[Appreciation] Analyze button clicked', { currentIndex, artwork: filteredArtworks[currentIndex] });
                                  try {
                                    const artworkToAnalyze = filteredArtworks[currentIndex];
                                    setSelectedArtwork(artworkToAnalyze);
                                    setShowAnalysis(true);
                                    setAppreciationResult(null); // Clear previous results
                                    setError(null); // Clear any previous errors
                                    console.log('[Appreciation] About to call handleAnalyze with artwork:', artworkToAnalyze);
                                    
                                    // Call handleAnalyze with the artwork directly instead of relying on state
                                    await handleAnalyzeWithArtwork(artworkToAnalyze);
                                    console.log('[Appreciation] handleAnalyze completed successfully');
                                  } catch (error) {
                                    console.error('[Appreciation] Error in analyze button click:', error);
                                    setError('Failed to start analysis. Please try again.');
                                  }
                                }}
                                disabled={isAnalyzing}
                                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isAnalyzing ? (
                                  <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Analyzing Artwork...
                                  </div>
                                ) : (
                                  'Analyze This Artwork'
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Error Display */}
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          {error}
                        </div>
                      </div>
                    )}

                    {/* Loading States */}
                    {hotspotsLoading && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-blue-700">
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                          Loading artwork details...
                        </div>
                      </div>
                    )}

                    {analysisLoading && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700">
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                          Generating appreciation analysis...
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Analysis Results Interface */
                  <div className="space-y-6">
                    {selectedArtwork && (
                      <div className="bg-amber-50 rounded-xl border border-amber-200 mb-4 md:mb-6 overflow-hidden">
                        {/* Mobile: Stack vertically, Desktop: Horizontal layout */}
                        <div className="flex flex-col md:flex-row md:items-center md:space-x-4 p-3 md:p-4 gap-3 md:gap-0">
                          {/* Artwork Thumbnail */}
                          <img
                            src={resolveImageUrl(selectedArtwork.imageUrl)}
                            alt={selectedArtwork.title}
                            className="w-full h-32 md:w-16 md:h-16 object-cover rounded-lg border border-amber-200 md:flex-shrink-0"
                            data-src={selectedArtwork.imageUrl}
                            onError={(e) => {
                              const current = e.currentTarget.getAttribute('data-src') || e.currentTarget.src || selectedArtwork?.imageUrl || '';
                              if (current && !current.includes('/proxy-image?') && /^https?:\/\//i.test(current)) {
                                e.currentTarget.src = `${API_ENDPOINTS.backendBase}/proxy-image?url=${encodeURIComponent(current)}`;
                              }
                            }}
                          />
                          {/* Artwork Details */}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 text-base md:text-lg mb-1 break-words">{selectedArtwork.title}</h3>
                            <p className="text-sm md:text-base text-gray-600 mb-2">{selectedArtwork.artistName}</p>
                            <div className="mb-2 md:mb-0">
                              <ClickableArtMovement 
                                artMovement={selectedArtwork.artMovement} 
                                className="text-xs md:text-sm"
                                onPopupStateChange={setIsArtMovementPopupOpen}
                              />
                            </div>
                          </div>
                          {/* Choose Different Artwork Button */}
                          <button
                            onClick={() => {
                              setShowAnalysis(false);
                              setAppreciationResult(null);
                              setSelectedArtwork(null);
                            }}
                            className="w-full md:w-auto px-4 py-2 md:py-1.5 text-sm md:text-base font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-100 rounded-lg transition-colors border border-amber-300 md:border-0 md:bg-transparent whitespace-nowrap"
                          >
                            Choose Different Artwork
                          </button>
                        </div>
                      </div>
                    )}


                    {/* Analysis Results */}
                    {appreciationResult && !isArtMovementPopupOpen && (
                      <div className="space-y-4 md:space-y-6">
                        {/* Artwork Container - Full Width */}
                        <div className="space-y-3 md:space-y-4">
                          <h3 className="text-base md:text-lg font-bold text-gray-900">Artwork Analysis</h3>
                          {selectedArtwork && (
                            <>
                              {/* Normal Artwork Display - Mobile responsive */}
                              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl md:rounded-2xl p-3 md:p-6 border-2 md:border-4 border-amber-200 shadow-xl relative">
                                {/* Expand Button - Only show when not expanded */}
                                {!isExpanded && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('[Appreciation] Expand button clicked in analysis view, current state:', isExpanded);
                                      setIsExpanded(!isExpanded);
                                    }}
                                    className="absolute top-2 right-2 md:top-4 md:right-4 p-2 md:p-3 bg-white hover:bg-gray-50 rounded-lg md:rounded-xl shadow-xl transition-all duration-200 border-2 border-gray-300 hover:border-amber-400"
                                    title="Expand Artwork"
                                    style={{ 
                                      minWidth: '36px', 
                                      minHeight: '36px',
                                      zIndex: 99999,
                                      isolation: 'isolate'
                                    }}
                                  >
                                    <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15.586 13H14a1 1 0 010-2h4z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                )}
                                
                                {!isExpanded && (
                                  <div className="w-full" style={{ height: '400px', maxHeight: '60vh', minHeight: '400px' }}>
                                    <ArtAnalysisHotspots
                                      imageUrl={selectedArtwork.imageUrl}
                                      analysisText={(() => {
                                        // Handle both 'appreciation' (array of strings) and 'points' (array of objects)
                                        if (appreciationResult?.appreciation && Array.isArray(appreciationResult.appreciation)) {
                                          return appreciationResult.appreciation.slice(0, 4);
                                        } else if (appreciationResult?.points && Array.isArray(appreciationResult.points)) {
                                          return appreciationResult.points.slice(0, 4).map(p => (typeof p === 'string' ? p : (p?.text || p?.label || '')));
                                        }
                                        return [];
                                      })()}
                                      title={selectedArtwork.title}
                                      enableFullscreen={true}
                                      layout="columnLeft"
                                      concepts={appreciationResult?.concepts || []}
                                      hideHotspots={isActivityPopupOpen}
                                    />
                                  </div>
                                )}
                              </div>

                              {/* Pop-out Artwork Overlay */}
                              {isExpanded && (
                                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
                                  <div className="relative w-[95vw] h-[85vh] max-w-6xl max-h-[85vh] bg-white rounded-2xl shadow-2xl border-4 border-amber-200 overflow-hidden">
                                    {/* Close Button for Pop-out - Top Right - Above Key Concepts - Small and Aesthetic */}
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('[Appreciation] Close pop-out button clicked');
                                        setIsExpanded(false);
                                      }}
                                      className="absolute top-3 right-3 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all duration-200 border border-gray-200 hover:border-amber-400 hover:shadow-lg"
                                      title="Close"
                                      style={{ 
                                        width: '28px', 
                                        height: '28px',
                                        zIndex: 99999,
                                        isolation: 'isolate',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <svg className="w-3.5 h-3.5 text-gray-600 hover:text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                    
                                    <div className="h-full flex items-center justify-center p-4">
                                      <div className="w-full h-full flex items-center justify-center">
                                        <ArtAnalysisHotspots
                                          imageUrl={selectedArtwork.imageUrl}
                                          analysisText={(() => {
                                            // Handle both 'appreciation' (array of strings) and 'points' (array of objects)
                                            if (appreciationResult?.appreciation && Array.isArray(appreciationResult.appreciation)) {
                                              return appreciationResult.appreciation.slice(0, 4);
                                            } else if (appreciationResult?.points && Array.isArray(appreciationResult.points)) {
                                              return appreciationResult.points.slice(0, 4).map(p => (typeof p === 'string' ? p : (p?.text || p?.label || '')));
                                            }
                                            return [];
                                          })()}
                                          title={selectedArtwork.title}
                                          enableFullscreen={false}
                                          layout="columnLeft"
                                          concepts={appreciationResult?.concepts || []}
                                          hideHotspots={isActivityPopupOpen}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Separate Activity Container — not in flow (activity is in Step 3) */}
                        {!embeddedInFlow && appreciationResult.activity && !isArtMovementPopupOpen && (
                          <div className="mt-6">
                            <ArtActivitySection 
                              activity={appreciationResult.activity}
                              artworkData={{
                                imageUrl: selectedArtwork.imageUrl,
                                analysisText: appreciationResult?.appreciation?.join('\n') || 'No analysis available',
                                title: selectedArtwork.title,
                              }}
                              onPopupStateChange={setIsActivityPopupOpen}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

    {/* Technique Drilldown Modal (Hotspot → Discovery Card → Chip tap) — collapsible sections */}
    {techniqueDrilldownModalOpen && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center z-[100001] p-4">
        <div className="bg-[#fefdf9] rounded-2xl shadow-2xl border border-amber-100/80 max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-amber-200/70 flex items-center justify-between bg-gradient-to-r from-amber-50/60 to-transparent">
            <h3 className="text-lg font-semibold text-amber-900 tracking-tight">
              {techniqueDrilldownLoading ? 'Loading…' : formatTechniqueDisplayName(techniqueDrilldown?.name || 'Technique')}
            </h3>
            <button
              type="button"
              onClick={() => { setTechniqueDrilldownModalOpen(false); setTechniqueDrilldown(null); setTechniqueSectionOpen(null); }}
              className="p-2 rounded-xl hover:bg-amber-100/80 text-amber-800 transition-colors"
              aria-label="Close"
            >
              <span className="text-xl leading-none">×</span>
            </button>
          </div>
          <div className="p-4 overflow-y-auto space-y-2">
            {techniqueDrilldownLoading && (
              <p className="text-amber-700/90 text-sm">Loading technique details…</p>
            )}
            {!techniqueDrilldownLoading && techniqueDrilldown && (
              <>
                {techniqueDrilldown.whatItIs && (
                  <div className="rounded-xl border border-amber-200/70 bg-white/80 overflow-hidden shadow-sm">
                    <button
                      type="button"
                      onClick={() => setTechniqueSectionOpen(s => s === 'whatItIs' ? null : 'whatItIs')}
                      className="w-full px-4 py-3 flex items-center justify-between gap-2 text-left hover:bg-amber-50/60 transition-colors"
                    >
                      <span className="text-sm font-semibold text-amber-900">What it is</span>
                      <span className={`text-amber-700 transition-transform ${techniqueSectionOpen === 'whatItIs' ? 'rotate-180' : ''}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </span>
                    </button>
                    <AnimatePresence>
                      {techniqueSectionOpen === 'whatItIs' && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 pt-0 text-sm text-amber-900/90 leading-relaxed border-t border-amber-100/80">
                            {techniqueDrilldown.whatItIs}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
                {techniqueDrilldown.howToTry && techniqueDrilldown.howToTry.length > 0 && (
                  <div className="rounded-xl border border-amber-200/70 bg-white/80 overflow-hidden shadow-sm">
                    <button
                      type="button"
                      onClick={() => setTechniqueSectionOpen(s => s === 'howToTry' ? null : 'howToTry')}
                      className="w-full px-4 py-3 flex items-center justify-between gap-2 text-left hover:bg-amber-50/60 transition-colors"
                    >
                      <span className="text-sm font-semibold text-amber-900">How to try it</span>
                      <span className={`text-amber-700 transition-transform ${techniqueSectionOpen === 'howToTry' ? 'rotate-180' : ''}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </span>
                    </button>
                    <AnimatePresence>
                      {techniqueSectionOpen === 'howToTry' && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 pt-0 text-sm text-amber-900/90 leading-relaxed border-t border-amber-100/80">
                            <ol className="list-decimal list-inside space-y-2">
                              {techniqueDrilldown.howToTry.map((step, i) => (
                                <li key={i} className="pl-1">{step}</li>
                              ))}
                            </ol>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    )}
  </>
  );

  return (
    <>
      <div className={embeddedInFlow ? 'h-full flex-1 min-h-0 flex flex-col' : 'min-h-screen h-screen flex flex-col relative bg-[#FFF8E1] px-0 py-0 md:px-[2.5cm] md:py-6'}>
        {!embeddedInFlow ? (
          <div className="relative z-10 w-full max-w-full flex-1 min-h-0 flex flex-col py-0 md:py-4 flex-grow">
            <div className="bg-white rounded-none md:rounded-[32px] shadow-2xl border-0 md:border-2 border-amber-300 flex flex-col flex-1 min-h-0 overflow-hidden min-h-[50vh]">
              <header className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-b border-amber-200/50 shadow-sm max-h-[72px] md:max-h-[80px]">
                <div className="flex items-center justify-between py-2 md:py-2.5 h-full min-h-[52px] px-3 md:px-6">
                  <p className="text-sm md:text-base font-semibold text-gray-800 truncate pr-2">Art History Appreciation</p>
                  <BackButton to={isFromMentorDashboard ? '/mentor-dashboard' : '/magic'} label="Back" />
                </div>
              </header>
              <div className="flex-1 min-h-0 overflow-auto max-w-7xl mx-auto w-full p-3 md:p-6">
                {mainContent}
              </div>
            </div>
          </div>
        ) : (
          mainContent
        )}
      </div>

    {/* Success Popup Modal */}
    {typeof document !== 'undefined' && createPortal(
      <AnimatePresence>
        {showSuccessPopup && uploadSuccessData && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[100002] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Upload Successful!</h3>
                <p className="text-gray-600">
                  {uploadSuccessData.shared 
                    ? `Your artwork for "${uploadSuccessData.artworkTitle}" has been uploaded and shared to Art History Learning Space!`
                    : `Your artwork for "${uploadSuccessData.artworkTitle}" has been uploaded successfully!`
                  }
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSuccessPopup(false);
                    if (uploadSuccessData.shared) {
                      navigate('/gallery/studio', { state: { activeTab: 'art-history' } });
                    }
                  }}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-3 px-6 rounded-lg hover:from-amber-600 hover:to-orange-600 transition-colors"
                >
                  {uploadSuccessData.shared ? 'View in Co-Learning Space' : 'OK'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>,
      document.body
    )}

    <UpgradeQuotaModal
      open={showUpgradeModal}
      onClose={dismissUpgradeModal}
      feature={upgradeModalFeature}
    />
    </>
  );
};

export default ArtHistoryAppreciation;
